import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multiselect';
import { ColorPicker } from '@/components/ui/color-picker';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSidebar } from '@/components/blocks/evo-sidebar';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Settings as SettingsIcon, Settings2, Clock, Users, Zap, Calendar, Shield, Globe, Palette, Trash2, Search, Copy, RefreshCw, ChevronDown, ChevronUp, FileText, Eye, CalendarClock, Plug, ChevronRight, BarChart3, RotateCcw, Filter, Plus, Upload, X, ChevronsUpDown, Check, User, Link, AlertTriangle, Save, Loader2, ShieldCheck, LayoutGrid } from 'lucide-react';
import { HelpButton } from '@/components/help/HelpButton';
import { useScreenData } from '@/hooks/useScreenData';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useUser } from '@/contexts/UserContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { Slider } from '@/components/ui/slider';

import { useTimezone } from '@/hooks/use-timezone';
import { CalendarView, ProjectStatusType, StatusConfig, BookingType, TabConfiguration, AppSettings, FieldFilterConfig } from '@/types';
import { toast } from 'sonner';
import { TIMEZONES } from '@/lib/timezones';
import { PROJECT_STATUSES } from '@/lib/statuses';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BookingTypeSetup as BookingTypeV2 } from '@/components/settings/BookingTypeSetup';
import { AuditSettings } from '@/components/settings/AuditSettings';
import { SettingsSaveBar } from '@/components/settings/SettingsSaveBar';
import { useAudit } from '@/contexts/AuditContext';
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder, generateDefaultTabConfigurations } from '@/utils/field-configurations';
import { Integrations } from './Integrations';
import { DataModeToggle } from '@/components/DataModeToggle';
import { bookingTypesApi, fieldConfigurationsApi, tabConfigurationsApi, calendarSyncApi, ApiError } from '@/services/api';
import { UtilisationSlider } from '@/components/UtilisationSlider';
import { DEFAULT_UTILIZATION_TIERS } from '@/utils/utilization-labels';
import { BookingTypeSetup } from '@/components/settings/BookingTypeSetup';
import { useConflictResolutionSettings, RESOLUTION_OPTION_CONFIGS, type ResolutionOptionId } from '@/hooks/useConflictResolutionSettings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { formatHourAsTime, parseTimeToHour } from '@/lib/hourOfDay';
import { RolesPermissionsList, type SecurityProfile } from '@/components/settings/RolesPermissionsList';
import { EditRoleProfile } from '@/components/settings/EditRoleProfile';
import { AccessDenied } from '@/components/AccessDenied';

const parseBool = (val: unknown): boolean => {
  if (val === true || val === 'true' || val === 1 || val === '1') return true;
  return false;
};

const normalizeFieldConfig = (fc: any) => ({
  fieldName: fc.fieldName || fc.FieldName || fc.fieldname || '',
  defaultName: fc.defaultName || fc.DefaultName || fc.fieldName || fc.FieldName || fc.fieldname || '',
  customName: fc.customName || fc.CustomName || '',
  isVisible: fc.isVisible != null ? parseBool(fc.isVisible) : (fc.IsVisible != null ? parseBool(fc.IsVisible) : (fc.visible != null ? parseBool(fc.visible) : (fc.Visible != null ? parseBool(fc.Visible) : true))),
  isCompulsory: fc.isCompulsory != null ? parseBool(fc.isCompulsory) : (fc.IsCompulsory != null ? parseBool(fc.IsCompulsory) : (fc.compulsory != null ? parseBool(fc.compulsory) : (fc.Compulsory != null ? parseBool(fc.Compulsory) : false))),
  showTooltip: fc.showTooltip != null ? parseBool(fc.showTooltip) : (fc.ShowTooltip != null ? parseBool(fc.ShowTooltip) : (fc.tooltip != null ? parseBool(fc.tooltip) : (fc.Tooltip != null ? parseBool(fc.Tooltip) : false))),
  showOnCustomTab: fc.showOnCustomTab != null ? parseBool(fc.showOnCustomTab) : (fc.ShowOnCustomTab != null ? parseBool(fc.ShowOnCustomTab) : false),
  placeholder: fc.placeholder || fc.Placeholder || '',
});

const normalizeTabConfig = (tc: any) => ({
  tabName: tc.tabName || tc.TabName || tc.tabname || '',
  defaultName: tc.defaultName || tc.DefaultName || tc.tabName || tc.TabName || tc.tabname || '',
  customName: tc.customName || tc.CustomName || '',
  isVisible: tc.isVisible != null ? parseBool(tc.isVisible) : (tc.IsVisible != null ? parseBool(tc.IsVisible) : (tc.visible != null ? parseBool(tc.visible) : (tc.Visible != null ? parseBool(tc.Visible) : true))),
});

const parseAndNormalizeFieldConfigs = (raw: unknown) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.map(normalizeFieldConfig).filter((f: any) => f.fieldName);
  } catch { return null; }
};

const parseAndNormalizeTabConfigs = (raw: unknown) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.map(normalizeTabConfig).filter((t: any) => t.tabName);
  } catch { return null; }
};

// Visible hours preview strip — extracted to avoid IIFE in JSX
interface VisibleHoursPreviewProps {
  visStart: number;
  bizStart: number;
  bizEnd: number;
  visEnd: number;
}
const VisibleHoursPreview = ({ visStart, bizStart, bizEnd, visEnd }: VisibleHoursPreviewProps) => {
  const preBuffer = bizStart - visStart;
  const business = bizEnd - bizStart;
  const postBuffer = visEnd - bizEnd;
  return (
    <div className="rounded-md border bg-muted/30 p-3 flex items-center gap-2 text-xs overflow-hidden">
      <span className="text-muted-foreground shrink-0">Preview:</span>
      <div className="flex-1 flex items-center h-5 rounded overflow-hidden">
        {preBuffer > 0 && (
          <div
            className="h-full bg-muted/70 flex items-center justify-center text-[10px] text-muted-foreground font-medium"
            style={{ flex: preBuffer }}
          >
            {visStart}:00
          </div>
        )}
        <div
          className="h-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-semibold"
          style={{ flex: business }}
        >
          {bizStart}:00 - {bizEnd}:00
        </div>
        {postBuffer > 0 && (
          <div
            className="h-full bg-muted/70 flex items-center justify-center text-[10px] text-muted-foreground font-medium"
            style={{ flex: postBuffer }}
          >
            {visEnd}:00
          </div>
        )}
      </div>
    </div>
  );
};

type AppPage = 'overview' | 'calendar' | 'heatmap' | 'resources' | 'projects' | 'settings' | 'open-demand-requests' | 'open-demand-unallocated' | 'dependent-fields' | 'report-utilisation' | 'report-utilisation-forecast' | 'report-available-capacity' | 'report-resource-allocation' | 'report-project-staffing' | 'report-bench' | 'report-audit-log' | 'roles-permissions' | 'roles-permissions-edit' | 'user-setup';
export function Settings({ initialTab, embeddedMode = false, hiddenTabs = [], onNavigate }: { initialTab?: string; embeddedMode?: boolean; hiddenTabs?: string[]; onNavigate?: (page: AppPage) => void }) {
  const { state: sidebarState } = useSidebar();
  const audit = useAudit();
  const sidebarLeft = sidebarState === 'collapsed' ? '3.5rem' : '13rem';
  const { settings: rawSettings, resources, bookings, updateSettings: persistSettings, currentMode, schedulerGroups, addSchedulerGroup, updateSchedulerGroup, saveSchedulerGroup, deleteSchedulerGroup } = useScreenData('settings');
  const { isLoading: isContextLoading, demoBookingTypeUsages, setDemoBookingTypeUsages, addSchedulerGroup: contextAddSchedulerGroup } = useScheduler();
  const { user } = useUser();
  const { enabled: resolutionOptions, isEnabled: isResolutionEnabled, toggle: toggleResolutionOption, whatIfEnabled, toggleWhatIf } = useConflictResolutionSettings();
  const { 
    timezone: effectiveTimezone, 
    setUserTimezone, 
    clearUserTimezone, 
    hasUserOverride, 
    systemDefault 
  } = useTimezone();
  const [searchQuery, setSearchQuery] = useState('');

  // Calendar sync state (moved from Integrations page)
  const [calendarUrl, setCalendarUrl] = useState<string>('');
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  const [isDeletingUrl, setIsDeletingUrl] = useState(false);
  const [isLoadingCalendarUrl, setIsLoadingCalendarUrl] = useState(false);
  const [isDeleteUrlDialogOpen, setIsDeleteUrlDialogOpen] = useState(false);

  // --- Deferred save: buffer changes in live mode on General Settings tab ---
  const [pendingSettings, setPendingSettings] = useState<Partial<AppSettings>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Pending conflict resolution state for Planning tab (localStorage-backed, not AppSettings)
  const [pendingWhatIfEnabled, setPendingWhatIfEnabled] = useState<boolean | null>(null);
  const [pendingResolutionOptions, setPendingResolutionOptions] = useState<Set<ResolutionOptionId> | null>(null);

  // Pending state for the Timezone tab (deferred save via shared save bar)
  const [pendingTimezoneSettings, setPendingTimezoneSettings] = useState<{ enableTimezones?: boolean; availableTimezones?: string[]; defaultTimezone?: string }>({});
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);
  const timezoneIsDirty = Object.keys(pendingTimezoneSettings).length > 0;

  const tzView = useMemo(() => ({
    enableTimezones: pendingTimezoneSettings.enableTimezones ?? rawSettings.enableTimezones,
    availableTimezones: pendingTimezoneSettings.availableTimezones ?? rawSettings.availableTimezones,
    defaultTimezone: pendingTimezoneSettings.defaultTimezone ?? rawSettings.defaultTimezone,
  }), [pendingTimezoneSettings, rawSettings.enableTimezones, rawSettings.availableTimezones, rawSettings.defaultTimezone]);

  const settings = useMemo(() => {
    if (currentMode !== 'live' || Object.keys(pendingSettings).length === 0) return rawSettings;
    return { ...rawSettings, ...pendingSettings } as AppSettings;
  }, [rawSettings, pendingSettings, currentMode]);
  
  // Ensure utilisationTiers is never empty - use defaults if not set
  const utilisationTiers = settings.utilisationTiers && settings.utilisationTiers.length > 0 
    ? settings.utilisationTiers 
    : DEFAULT_UTILIZATION_TIERS;
  
  // Debug logging for timezone state on mount and updates
  useEffect(() => {
    console.log('🔝 [TIMEZONE STATE] Settings loaded:', {
      mode: currentMode,
      defaultTimezone: settings.defaultTimezone,
      timelineDefaultTimezone: settings.timelineDefaultTimezone,
      availableTimezones: settings.availableTimezones,
      effectiveTimezone,
      systemDefault,
      hasUserOverride,
      userId: user?.id,
    });
  }, [settings.defaultTimezone, effectiveTimezone, systemDefault, hasUserOverride, currentMode, user?.id]);
  
  // Use the imported function for default tab configurations
  const getDefaultTabConfigurations = generateDefaultTabConfigurations;

  const [enrichedConfigs, setEnrichedConfigs] = useState<Record<string, { fieldConfigurations: BookingType['fieldConfigurations']; tabConfigurations: BookingType['tabConfigurations'] }>>({});
  
  // Migrate existing booking types to include showOnCustomTab property
  // Merge in enrichedConfigs fetched from the dedicated field/tab config API tables
  const migratedBookingTypes = settings.bookingTypes?.map(bt => {
    const btId = String(bt.id);
    const enriched = enrichedConfigs[btId];
    const fieldConfigs = enriched?.fieldConfigurations || bt.fieldConfigurations || generateDefaultFieldConfigurations();
    const tabConfigs = enriched?.tabConfigurations || bt.tabConfigurations || getDefaultTabConfigurations();
    
    const updatedFields = fieldConfigs.map(field => {
      if (field.showOnCustomTab === undefined) {
        return { ...field, showOnCustomTab: false };
      }
      return field;
    });
    
    return { 
      ...bt, 
      id: btId,
      fieldConfigurations: updatedFields,
      tabConfigurations: tabConfigs
    };
  }) || [];
  
  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState<string>(
    String(migratedBookingTypes[0]?.id ?? '')
  );
  const [activeTab, setActiveTab] = useState(initialTab ?? 'settings');
  const [adminSubTab, setAdminSubTab] = useState('timezone');
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [generalSubTab, setGeneralSubTab] = useState('heatmap-settings');
  const [generalTabsWithChanges, setGeneralTabsWithChanges] = useState<Set<string>>(new Set());
  const [generalHasUnsavedChanges, setGeneralHasUnsavedChanges] = useState(false);

  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const generalSubTabRef = useRef(generalSubTab);
  generalSubTabRef.current = generalSubTab;

  const updateSettings = useCallback(async (changes: Partial<AppSettings>) => {
    if (currentMode === 'live' && activeTabRef.current === 'settings') {
      setPendingSettings(prev => ({ ...prev, ...changes }));
      setGeneralHasUnsavedChanges(true);
      setGeneralTabsWithChanges(prev => new Set(prev).add(generalSubTabRef.current));
    } else {
      await persistSettings(changes);
    }
  }, [currentMode, persistSettings]);

  const markGeneralTabAsChanged = useCallback((tabId: string) => {
    setGeneralTabsWithChanges(prev => new Set(prev).add(tabId));
    setGeneralHasUnsavedChanges(true);
  }, []);

  // Effective conflict resolution values (pending or actual)
  const effectiveWhatIfEnabled = pendingWhatIfEnabled !== null ? pendingWhatIfEnabled : whatIfEnabled;
  const effectiveIsResolutionEnabled = useCallback((id: ResolutionOptionId) => {
    if (pendingResolutionOptions !== null) return pendingResolutionOptions.has(id);
    return isResolutionEnabled(id);
  }, [pendingResolutionOptions, isResolutionEnabled]);

  const handleToggleWhatIf = useCallback(() => {
    if (currentMode === 'live') {
      const current = pendingWhatIfEnabled !== null ? pendingWhatIfEnabled : whatIfEnabled;
      setPendingWhatIfEnabled(!current);
      setGeneralHasUnsavedChanges(true);
      setGeneralTabsWithChanges(prev => new Set(prev).add('planning'));
    } else {
      toggleWhatIf();
    }
  }, [currentMode, pendingWhatIfEnabled, whatIfEnabled, toggleWhatIf]);

  const handleToggleResolutionOption = useCallback((id: ResolutionOptionId) => {
    if (currentMode === 'live') {
      const current = pendingResolutionOptions !== null ? new Set(pendingResolutionOptions) : new Set(resolutionOptions);
      if (current.has(id) && current.size === 1) return;
      if (current.has(id)) current.delete(id);
      else current.add(id);
      setPendingResolutionOptions(current);
      setGeneralHasUnsavedChanges(true);
      setGeneralTabsWithChanges(prev => new Set(prev).add('planning'));
    } else {
      toggleResolutionOption(id);
    }
  }, [currentMode, pendingResolutionOptions, resolutionOptions, toggleResolutionOption]);

  // Reset pending changes when data mode changes
  useEffect(() => {
    setPendingSettings({});
    setPendingWhatIfEnabled(null);
    setPendingResolutionOptions(null);
    setPendingTimezoneSettings({});
    setGeneralTabsWithChanges(new Set());
    setGeneralHasUnsavedChanges(false);
  }, [currentMode]);

  // Warn before browser close/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (generalHasUnsavedChanges || timezoneIsDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [generalHasUnsavedChanges, timezoneIsDirty]);

  const handleSaveAllSettings = useCallback(async () => {
    const hasPendingAppSettings = Object.keys(pendingSettings).length > 0;
    const hasPendingConflictRes = pendingWhatIfEnabled !== null || pendingResolutionOptions !== null;
    if (!hasPendingAppSettings && !hasPendingConflictRes) return;
    setIsSaving(true);
    try {
      if (hasPendingAppSettings) {
        await persistSettings(pendingSettings);
      }
      // Apply conflict resolution changes to localStorage
      if (pendingWhatIfEnabled !== null && pendingWhatIfEnabled !== whatIfEnabled) {
        toggleWhatIf();
      }
      if (pendingResolutionOptions !== null) {
        for (const opt of RESOLUTION_OPTION_CONFIGS) {
          const shouldBeEnabled = pendingResolutionOptions.has(opt.id);
          const currentlyEnabled = isResolutionEnabled(opt.id);
          if (shouldBeEnabled !== currentlyEnabled) toggleResolutionOption(opt.id);
        }
      }
      // Audit log for general settings changes
      const changes: { field: string; before: string | null; after: string | null }[] = Object.entries(pendingSettings).map(([key, val]) => ({
        field: key,
        before: null as string | null,
        after: String(val ?? ''),
      }));
      if (pendingWhatIfEnabled !== null && pendingWhatIfEnabled !== whatIfEnabled) {
        changes.push({ field: 'What-If Planning', before: String(whatIfEnabled), after: String(pendingWhatIfEnabled) });
      }
      if (changes.length > 0) {
        audit.log({
          user: 'Current User',
          category: 'Settings – General',
          action: 'Edit',
          entity: 'General Settings',
          changes,
        });
      }

      setPendingSettings({});
      setPendingWhatIfEnabled(null);
      setPendingResolutionOptions(null);
      setGeneralTabsWithChanges(new Set());
      setGeneralHasUnsavedChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [pendingSettings, persistSettings, pendingWhatIfEnabled, whatIfEnabled, toggleWhatIf, pendingResolutionOptions, isResolutionEnabled, toggleResolutionOption, audit]);

  const handleCancelSettings = useCallback(() => {
    if (generalHasUnsavedChanges) {
      setShowCancelDialog(true);
    }
  }, [generalHasUnsavedChanges]);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelDialog(false);
    setPendingSettings({});
    setPendingWhatIfEnabled(null);
    setPendingResolutionOptions(null);
    setGeneralTabsWithChanges(new Set());
    setGeneralHasUnsavedChanges(false);
  }, []);

  const SECURITY_PROFILES: SecurityProfile[] = [
    { id: 1, name: 'Administrator', description: 'Full system access and control', isSystem: true, isActive: true },
    { id: 2, name: 'User', description: 'Standard user access for scheduling features', isSystem: true, isActive: true },
  ];
  const selectedRole = SECURITY_PROFILES.find((r) => r.id === selectedRoleId) ?? null;

  // Sync activeTab when sidebar navigation changes the initialTab prop
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Reset role selection when navigating away from admin tab
  useEffect(() => {
    if (activeTab !== 'admin') {
      setSelectedRoleId(null);
    }
  }, [activeTab]);
  
  
  // Local state for business hours to only save on blur
  const [localBusinessHoursStart, setLocalBusinessHoursStart] = useState<string>('');
  const [localBusinessHoursEnd, setLocalBusinessHoursEnd] = useState<string>('');

  // Local state for visible hours to only save on blur
  const [localVisibleHoursStart, setLocalVisibleHoursStart] = useState<string>('');
  const [localVisibleHoursEnd, setLocalVisibleHoursEnd] = useState<string>('');

  // Initialize local business hours from settings
  useEffect(() => {
    setLocalBusinessHoursStart(formatHourAsTime(settings.businessHoursStart, '09:00'));
    setLocalBusinessHoursEnd(formatHourAsTime(settings.businessHoursEnd, '17:00'));
  }, [settings.businessHoursStart, settings.businessHoursEnd]);

  // Initialize local visible hours from settings
  useEffect(() => {
    setLocalVisibleHoursStart(formatHourAsTime(settings.visibleHoursStart ?? 7, '07:00'));
    setLocalVisibleHoursEnd(formatHourAsTime(settings.visibleHoursEnd ?? 19, '19:00'));
  }, [settings.visibleHoursStart, settings.visibleHoursEnd]);

    const [isLoadingBookingType, setIsLoadingBookingType] = useState(false);

    // Reset enriched configs cache and selected booking type when the company changes
    // so the Booking Type Setup screen shows data for the newly selected company.
    const { currentCompany } = useCompany();
    const currentCompanyIdForSettings = currentCompany?.id;
    const prevCompanyIdForSettingsRef = useRef(currentCompanyIdForSettings);
    useEffect(() => {
      if (prevCompanyIdForSettingsRef.current === currentCompanyIdForSettings) return;
      prevCompanyIdForSettingsRef.current = currentCompanyIdForSettings;
      // Clear cached field/tab configs so they are re-fetched for the new company
      setEnrichedConfigs({});
      // Reset selected booking type so the auto-select effect picks the first one for the new company
      setSelectedBookingTypeId('');
    }, [currentCompanyIdForSettings]);

    // Auto-select default booking type when settings.bookingTypes becomes available in Live mode
    useEffect(() => {
      if (currentMode !== 'live') return;
      const btList = settings.bookingTypes;
      if (!btList || btList.length === 0) return;
      if (selectedBookingTypeId && btList.find(bt => String(bt.id) === String(selectedBookingTypeId))) return;
      const defaultBt = btList.find(bt => bt.name?.toLowerCase() === 'project work') || btList[0];
      if (defaultBt) {
        setSelectedBookingTypeId(String(defaultBt.id));
      }
    }, [currentMode, settings.bookingTypes]);

    useEffect(() => {
      if (currentMode !== 'live' || !selectedBookingTypeId) return;
      const btId = Number(selectedBookingTypeId);
      if (isNaN(btId)) return;
      if (enrichedConfigs[selectedBookingTypeId]) return;

      setIsLoadingBookingType(true);
      Promise.all([
        fieldConfigurationsApi.getByBookingTypeId(btId).catch(() => []),
        tabConfigurationsApi.getByBookingTypeId(btId).catch(() => []),
      ]).then(([dbFieldConfigs, dbTabConfigs]) => {
        let fieldConfigurations = generateDefaultFieldConfigurations();
        let tabConfigurations = generateDefaultTabConfigurations();

        if (Array.isArray(dbFieldConfigs) && dbFieldConfigs.length > 0) {
          fieldConfigurations = dbFieldConfigs.map((fc: any) => {
            const rules = fc.validationRules || fc.ValidationRules;
            let extra: Record<string, unknown> = {};
            if (rules && typeof rules === 'string') { try { extra = JSON.parse(rules); } catch { /* ignore */ } }
            return {
              fieldName: fc.fieldName || fc.FieldName || '',
              defaultName: fc.fieldName || fc.FieldName || '',
              placeholder: fc.placeholder || fc.Placeholder || '',
              defaultPlaceholder: fc.defaultPlaceholder || fc.DefaultPlaceholder || fc.placeholder || fc.Placeholder || '',
              isVisible: fc.isVisible != null ? parseBool(fc.isVisible) : (fc.IsVisible != null ? parseBool(fc.IsVisible) : true),
              isRequired: fc.isRequired != null ? parseBool(fc.isRequired) : (fc.IsRequired != null ? parseBool(fc.IsRequired) : false),
              isCompulsory: fc.isCompulsory != null ? parseBool(fc.isCompulsory) : (fc.IsCompulsory != null ? parseBool(fc.IsCompulsory) : false),
              showTooltip: extra.showTooltip != null ? parseBool(extra.showTooltip) : false,
              showOnCustomTab: extra.showOnCustomTab != null ? parseBool(extra.showOnCustomTab) : false,
              filterConfig: (extra.filterConfig as FieldFilterConfig) || undefined,
              customName: fc.customName || fc.CustomName || '',
            };
          });

          // Restore project filter config from the booking type record (canonical source)
          const selectedBt = settings.bookingTypes.find(bt => String(bt.id) === String(selectedBookingTypeId));
          if (selectedBt?.projectFilterConfig) {
            fieldConfigurations = fieldConfigurations.map(fc =>
              fc.fieldName?.toLowerCase() === 'project'
                ? { ...fc, filterConfig: selectedBt.projectFilterConfig }
                : fc
            );
          }
        }
        if (Array.isArray(dbTabConfigs) && dbTabConfigs.length > 0) {
          tabConfigurations = dbTabConfigs.map((tc: any) => ({
            tabName: tc.tabName || tc.TabName || '',
            defaultName: tc.defaultName || tc.DefaultName || '',
            customName: tc.customName || tc.CustomName || '',
            isVisible: tc.isVisible != null ? parseBool(tc.isVisible) : (tc.IsVisible != null ? parseBool(tc.IsVisible) : true),
          }));
        }

        setEnrichedConfigs(prev => ({ ...prev, [selectedBookingTypeId]: { fieldConfigurations, tabConfigurations } }));
      }).catch(e => {
        console.error('Failed to fetch field/tab configs for booking type', btId, e);
      }).finally(() => {
        setIsLoadingBookingType(false);
      });
    }, [currentMode, selectedBookingTypeId, enrichedConfigs]);
  
    // Accordion state management - only one section can be open at a time per tab
  // Settings Tab sections
  const [openSettingsSection, setOpenSettingsSection] = useState<string | null>(null);
  
  // Helper functions for accordion behavior (user settings sections still use Collapsible)
  const handleSettingsSectionToggle = (section: string) => {
    setOpenSettingsSection(prev => prev === section ? null : section);
  };
  
  
  const { canViewSettingsTab, canViewRolesPermissions, canViewIntegrations, hasFullAccess } = usePermissions();
  const isAdministrator = hasFullAccess || canViewSettingsTab('viewGeneral');
  const selectedBookingType = migratedBookingTypes.find(bt => String(bt.id) === String(selectedBookingTypeId));



  const handleDefaultViewChange = (view: CalendarView) => {
    // Update both defaultCalendarView and timelineDefaultTimeRange to keep them in sync
    const timeRange = view.charAt(0).toUpperCase() + view.slice(1); // Capitalize first letter
    updateSettings({ 
      defaultCalendarView: view,
      timelineDefaultTimeRange: timeRange 
    });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleAbsenceToggle = (checked: boolean) => {
    if (!checked) {
      updateSettings({ showAbsence: checked, absenceLabelOverride: '' });
    } else {
      updateSettings({ showAbsence: checked });
    }
    // Toast removed - toggle state provides visual feedback
  };

  const handleAbsenceLabelChange = (value: string) => {
    updateSettings({ absenceLabelOverride: value });
    // Toast removed - input value provides visual feedback
  };

  const handleAbsenceColorChange = (color: string) => {
    updateSettings({ absenceColor: color });
    // Toast removed - color picker provides visual feedback
  };

  const handlePublicHolidaysToggle = (checked: boolean) => {
    updateSettings({ showPublicHolidays: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handlePublicHolidayColorChange = (color: string) => {
    updateSettings({ publicHolidayColor: color });
    // Toast removed - color picker provides visual feedback
  };

  const handleGroupingMethodChange = (value: 'scheduler-group' | 'department') => {
    updateSettings({ groupingMethod: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleConfirmDragDropToggle = (checked: boolean) => {
    updateSettings({ confirmDragDrop: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleDefaultToLoggedInResourceToggle = (checked: boolean) => {
    updateSettings({ defaultToLoggedInResource: checked });
    // Toast removed - toggle state provides visual feedback
  };

  // User Preference Handlers
  const handleUserConfirmDragDropToggle = (checked: boolean) => {
    updateSettings({ userConfirmDragDrop: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleUserDefaultToLoggedInResourceToggle = (checked: boolean) => {
    updateSettings({ userDefaultToLoggedInResource: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleShowResourceRatesToggle = (checked: boolean) => {
    if (!checked) {
      // When disabling, also disable copy resource rates
      updateSettings({ showResourceRates: checked, copyResourceRates: false });
    } else {
      updateSettings({ showResourceRates: checked });
    }
    // Toast removed - toggle state provides visual feedback
  };

  const handleCopyResourceRatesToggle = (checked: boolean) => {
    updateSettings({ copyResourceRates: checked });
    // Toast removed - toggle state provides visual feedback
  };

  const handleRagThresholdChange = (field: 'greenThreshold' | 'amberThreshold', value: string) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 0 || num > 100) return;
    const current = settings.aiRagThresholds ?? { greenThreshold: 80, amberThreshold: 50 };
    updateSettings({ aiRagThresholds: { ...current, [field]: num } });
  };

  const handleResetRagThresholds = () => {
    updateSettings({ aiRagThresholds: { greenThreshold: 80, amberThreshold: 50 } });
    toast.success('Match thresholds reset to defaults');
  };

  // Timezone tab edits are buffered and persisted on Save via the shared save bar.
  const handleTimezonesToggle = (checked: boolean) => {
    setPendingTimezoneSettings(prev => ({ ...prev, enableTimezones: checked }));
  };

  const handleTimezonesChange = (timezones: string[]) => {
    setPendingTimezoneSettings(prev => ({ ...prev, availableTimezones: timezones }));
  };

  const handleDefaultTimezoneChange = (value: string) => {
    setPendingTimezoneSettings(prev => ({ ...prev, defaultTimezone: value }));
  };

  const handleSaveTimezoneSettings = useCallback(async () => {
    if (Object.keys(pendingTimezoneSettings).length === 0) return;
    setIsSavingTimezone(true);
    try {
      const changes: { field: string; before: string | null; after: string | null }[] = [];
      const updates: Partial<AppSettings> = {};

      if (pendingTimezoneSettings.enableTimezones !== undefined) {
        updates.enableTimezones = pendingTimezoneSettings.enableTimezones;
        changes.push({ field: 'Multiple Timezones', before: String(rawSettings.enableTimezones), after: String(pendingTimezoneSettings.enableTimezones) });
      }
      if (pendingTimezoneSettings.availableTimezones !== undefined) {
        updates.availableTimezones = pendingTimezoneSettings.availableTimezones;
        changes.push({ field: 'Available Timezones', before: (rawSettings.availableTimezones ?? []).join(', '), after: pendingTimezoneSettings.availableTimezones.join(', ') });
      }
      if (pendingTimezoneSettings.defaultTimezone !== undefined) {
        updates.defaultTimezone = pendingTimezoneSettings.defaultTimezone;
        updates.timelineDefaultTimezone = pendingTimezoneSettings.defaultTimezone;
        changes.push({ field: 'Default Timezone', before: rawSettings.defaultTimezone ?? systemDefault, after: pendingTimezoneSettings.defaultTimezone });
      }

      if (Object.keys(updates).length > 0) {
        await persistSettings(updates);
      }
      if (pendingTimezoneSettings.defaultTimezone !== undefined) {
        if (hasUserOverride) {
          await clearUserTimezone();
        }
        await setUserTimezone(pendingTimezoneSettings.defaultTimezone);
      }

      if (changes.length > 0) {
        audit.log({
          user: 'Current User',
          category: 'Settings – Timezones',
          action: 'Edit',
          entity: 'Timezone Settings',
          changes,
        });
      }

      setPendingTimezoneSettings({});
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save timezone', {
        description: error instanceof Error ? error.message : 'Please check your connection and try again.',
      });
    } finally {
      setIsSavingTimezone(false);
    }
  }, [pendingTimezoneSettings, rawSettings.enableTimezones, rawSettings.availableTimezones, rawSettings.defaultTimezone, systemDefault, persistSettings, hasUserOverride, clearUserTimezone, setUserTimezone, audit]);

  const handleDiscardTimezoneSettings = useCallback(() => {
    setPendingTimezoneSettings({});
  }, []);

  const handleQuickAddDefaultChange = (value: string) => {
    updateSettings({ quickAddDefault: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleDescriptionDefaultChange = (value: string) => {
    updateSettings({ descriptionDefault: value as 'none' | 'from-project' | 'from-project-description' });
    // Toast removed - dropdown selection provides visual feedback
  };

  const allProjectStatusValues = PROJECT_STATUSES.map(s => s.value);

  const handleProjectStatusesChange = (values: string[]) => {
    const prevHadAll = settings.availableProjectStatuses === 'all';
    const newHasAll = values.includes('all');

    if (!prevHadAll && newHasAll) {
      updateSettings({ availableProjectStatuses: 'all' });
    } else if (prevHadAll && !newHasAll) {
      updateSettings({ availableProjectStatuses: [] as ProjectStatusType[] });
    } else if (prevHadAll && newHasAll) {
      const withoutAll = values.filter(v => v !== 'all') as ProjectStatusType[];
      updateSettings({ availableProjectStatuses: withoutAll });
    } else {
      const allIndividualsSelected = allProjectStatusValues.every(v => values.includes(v));
      if (allIndividualsSelected) {
        updateSettings({ availableProjectStatuses: 'all' });
      } else {
        updateSettings({ availableProjectStatuses: values as ProjectStatusType[] });
      }
    }
  };

  const handleCostCentreStatusesChange = (values: string[]) => {
    const prevHadAll = settings.availableCostCentreStatuses === 'all';
    const newHasAll = values.includes('all');

    if (!prevHadAll && newHasAll) {
      updateSettings({ availableCostCentreStatuses: 'all' });
    } else if (prevHadAll && !newHasAll) {
      updateSettings({ availableCostCentreStatuses: [] as ProjectStatusType[] });
    } else if (prevHadAll && newHasAll) {
      const withoutAll = values.filter(v => v !== 'all') as ProjectStatusType[];
      updateSettings({ availableCostCentreStatuses: withoutAll });
    } else {
      const allIndividualsSelected = allProjectStatusValues.every(v => values.includes(v));
      if (allIndividualsSelected) {
        updateSettings({ availableCostCentreStatuses: 'all' });
      } else {
        updateSettings({ availableCostCentreStatuses: values as ProjectStatusType[] });
      }
    }
  };

  const handleStatusTextChange = (id: string, text: string) => {
    // Define default status texts
    const defaultStatusTexts: Record<string, string> = {
      'planned': 'Planned',
      'confirmed': 'Confirmed',
      'cancelled': 'Cancelled',
      'completed': 'Completed',
    };
    
    const updatedConfigs = settings.statusConfigs.map(config => {
      if (String(config.id) === String(id)) {
        // If text is empty, revert to default
        const finalText = text.trim() === '' ? (defaultStatusTexts[id] || config.text) : text;
        return { ...config, text: finalText };
      }
      return config;
    });
    updateSettings({ statusConfigs: updatedConfigs });
  };

  const handleStatusColorChange = (id: string | number, color: string) => {
    const updatedConfigs = settings.statusConfigs.map(config =>
      String(config.id) === String(id) ? { ...config, color } : config
    );
    updateSettings({ statusConfigs: updatedConfigs });
    // Toast removed - color picker provides visual feedback
  };

  const handleStatusEnabledChange = (id: string | number, enabled: boolean) => {
    const updatedConfigs = settings.statusConfigs.map(config =>
      String(config.id) === String(id) ? { ...config, enabled } : config
    );
    updateSettings({ statusConfigs: updatedConfigs });
    // Toast removed - checkbox state provides visual feedback
  };

  const handleStatusCustomTextChange = (id: string | number, customText: string) => {
    const updatedConfigs = settings.statusConfigs.map(config =>
      String(config.id) === String(id) ? { ...config, customText } : config
    );
    updateSettings({ statusConfigs: updatedConfigs });
  };

  const handleStatusCustomTextBlur = (id: string | number) => {
    const status = settings.statusConfigs.find(config => String(config.id) === String(id));
    if (status) {
      const finalCustomText = status.customText?.trim() === '' ? undefined : status.customText;
      const updatedConfigs = settings.statusConfigs.map(config =>
        String(config.id) === String(id) ? { ...config, customText: finalCustomText } : config
      );
      updateSettings({ statusConfigs: updatedConfigs });
    }
  };

  const handleColorOverrideTextChange = (id: string, text: string) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, text } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
  };

  const handleColorOverrideColorChange = (id: string | number, color: string) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, color } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
    // Toast removed - color picker provides visual feedback
  };

  const handleColorOverrideEnabledChange = (id: string | number, enabled: boolean) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, enabled } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
  };

  const handleColorOverrideCustomTextChange = (id: string | number, customText: string) => {
    const updatedOverrides = settings.colorOverrides.map(override =>
      String(override.id) === String(id) ? { ...override, customText } : override
    );
    updateSettings({ colorOverrides: updatedOverrides });
  };

  const handleColorOverrideCustomTextBlur = (id: string | number) => {
    const override = settings.colorOverrides.find(o => String(o.id) === String(id));
    if (override) {
      const finalCustomText = override.customText?.trim() === '' ? undefined : override.customText;
      const updatedOverrides = settings.colorOverrides.map(o =>
        String(o.id) === String(id) ? { ...o, customText: finalCustomText } : o
      );
      updateSettings({ colorOverrides: updatedOverrides });
    }
  };

  const handleAddColorOverride = () => {
    const newOverride = {
      id: `override-${Date.now()}`,
      text: 'Override Text',
      color: '#85B7EB',
      enabled: true,
    };
    updateSettings({ colorOverrides: [...settings.colorOverrides, newOverride] });
    // Toast removed - new override appears in list
  };

  const handleDeleteColorOverride = (id: string | number) => {
    const updatedOverrides = settings.colorOverrides.filter(override => String(override.id) !== String(id));
    updateSettings({ colorOverrides: updatedOverrides });
    // Toast removed - override disappears from list
  };

  // Utilisation Tier handlers
  const handleUtilisationTierLabelChange = (id: string | number, customLabel: string) => {
    const updatedTiers = settings.utilisationTiers.map(tier =>
      String(tier.id) === String(id) ? { ...tier, customLabel } : tier
    );
    updateSettings({ utilisationTiers: updatedTiers });
  };

  const handleUtilisationTierLabelBlur = (id: string | number) => {
    const tier = settings.utilisationTiers.find(t => String(t.id) === String(id));
    if (tier) {
      const finalCustomLabel = tier.customLabel?.trim() === '' ? undefined : tier.customLabel;
      const updatedTiers = settings.utilisationTiers.map(t =>
        String(t.id) === String(id) ? { ...t, customLabel: finalCustomLabel } : t
      );
      updateSettings({ utilisationTiers: updatedTiers });
    }
  };

  const handleUtilisationTierColorChange = (id: string | number, color: string) => {
    const updatedTiers = settings.utilisationTiers.map(tier =>
      String(tier.id) === String(id) ? { ...tier, color } : tier
    );
    updateSettings({ utilisationTiers: updatedTiers });
  };

  const handleUtilisationBoundaryChange = (leftTierId: string | number, rightTierId: string | number, boundaryValue: number) => {
  const updatedTiers = utilisationTiers.map((t) =>
    String(t.id) === String(leftTierId)
      ? { ...t, maxPercentage: Math.max(t.minPercentage, boundaryValue - 1) }
      : String(t.id) === String(rightTierId)
        ? { ...t, minPercentage: Math.min(t.maxPercentage, boundaryValue) }
        : t
  );
  updateSettings({ utilisationTiers: updatedTiers });
};

const handleUtilisationTierToggle = (id: string | number, enabled: boolean) => {
  const toggled = utilisationTiers.map((t) => (String(t.id) === String(id) ? { ...t, enabled } : t));
  const enabledBands = toggled.filter((t) => t.enabled).sort((a, b) => a.minPercentage - b.minPercentage);

  if (enabledBands.length >= 1) {
    // Detect whether any band is intended to capture over-utilisation (>100%).
    // If so, the last enabled band keeps its maxPercentage (999) so it
    // captures everything above 100%. Otherwise we distribute 0–100 evenly.
    const hasOverBand = enabledBands.some((b) => b.maxPercentage > 100);
    const subHundredBands = hasOverBand ? enabledBands.slice(0, -1) : enabledBands;
    const overBand = hasOverBand ? enabledBands[enabledBands.length - 1] : null;

    const globalMin = 0;
    const globalMax = 100;
    const bandWidth = subHundredBands.length > 0
      ? (globalMax - globalMin) / subHundredBands.length
      : 0;

    const redistributed = toggled.map((t) => {
      if (!t.enabled) return t;
      // The over-utilisation band always starts at 101 and extends to 999
      if (overBand && String(t.id) === String(overBand.id)) {
        return { ...t, minPercentage: 101, maxPercentage: 999 };
      }
      const idx = subHundredBands.findIndex((b) => String(b.id) === String(t.id));
      if (idx === -1) return t;
      const newMin = idx === 0 ? globalMin : Math.ceil(globalMin + idx * bandWidth);
      const newMax =
        idx === subHundredBands.length - 1
          ? globalMax
          : Math.floor(globalMin + (idx + 1) * bandWidth - 0.01);
      return { ...t, minPercentage: newMin, maxPercentage: newMax };
    });
    updateSettings({ utilisationTiers: redistributed });
  } else {
    updateSettings({ utilisationTiers: toggled });
  }
};

const handleUtilisationTierRangeChange = (id: string, field: 'minPercentage' | 'maxPercentage', value: string) => {
    const numValue = parseInt(value);
    // Allow values from 0 up to 999 so over-utilisation bands (>100%) are supported
    if (isNaN(numValue) || numValue < 0 || numValue > 999) return;
    
    const updatedTiers = settings.utilisationTiers.map(tier =>
      String(tier.id) === String(id) ? { ...tier, [field]: numValue } : tier
    );
    updateSettings({ utilisationTiers: updatedTiers });
  };

  const handleResetUtilisationTiers = () => {
    updateSettings({ utilisationTiers: DEFAULT_UTILIZATION_TIERS });
    toast.success('Utilisation bands reset to defaults');
  };

  // Validation function for utilisation tiers
  // Removed: validateUtilisationTiers function (Demo only)

  const timezoneOptions = TIMEZONES.map(tz => ({
    value: tz.value,
    label: tz.label
  }));

  const handleBusinessHoursStartChange = (value: string) => {
    setLocalBusinessHoursStart(value);
  };

  const handleBusinessHoursEndChange = (value: string) => {
    setLocalBusinessHoursEnd(value);
  };
  
  const handleBusinessHoursStartBlur = () => {
    const hour = parseTimeToHour(localBusinessHoursStart);
    if (hour === null) {
      setLocalBusinessHoursStart(formatHourAsTime(settings.businessHoursStart, '09:00'));
      return;
    }
    updateSettings({ businessHoursStart: hour });
  };

  const handleBusinessHoursEndBlur = () => {
    const hour = parseTimeToHour(localBusinessHoursEnd);
    if (hour === null) {
      setLocalBusinessHoursEnd(formatHourAsTime(settings.businessHoursEnd, '17:00'));
      return;
    }
    updateSettings({ businessHoursEnd: hour });
  };

  const handleVisibleHoursStartChange = (value: string) => {
    setLocalVisibleHoursStart(value);
  };

  const handleVisibleHoursEndChange = (value: string) => {
    setLocalVisibleHoursEnd(value);
  };

  const handleVisibleHoursStartBlur = () => {
    const hour = parseTimeToHour(localVisibleHoursStart);
    if (hour === null) {
      setLocalVisibleHoursStart(formatHourAsTime(settings.visibleHoursStart, '07:00'));
      return;
    }
    updateSettings({ visibleHoursStart: hour });
  };

  const handleVisibleHoursEndBlur = () => {
    const hour = parseTimeToHour(localVisibleHoursEnd);
    if (hour === null) {
      setLocalVisibleHoursEnd(formatHourAsTime(settings.visibleHoursEnd, '19:00'));
      return;
    }
    updateSettings({ visibleHoursEnd: hour });
  };











  // Timeline Options handlers
  const handleTimelineTimeRangeChange = (value: string) => {
    // Update both timelineDefaultTimeRange and defaultCalendarView to keep them in sync
    const calendarView = value.toLowerCase() as CalendarView;
    updateSettings({ 
      timelineDefaultTimeRange: value,
      defaultCalendarView: calendarView 
    });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleTimelineBookingTypeChange = (value: string) => {
    updateSettings({ timelineDefaultBookingType: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleTimelineBookingStatusChange = (value: string) => {
    updateSettings({ timelineDefaultBookingStatus: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  // Normalize legacy timeline view values to current format
  const normalizeTimelineView = (value: string | undefined): string => {
    if (!value) return 'resource';
    const normalized = value.toLowerCase();
    if (normalized === 'customer') return 'customer';
    if (normalized === 'project & resource' || normalized === 'project-resource') return 'project-resource';
    if (normalized === 'resource') return 'resource';
    if (normalized === 'role & resource' || normalized === 'role-resource') return 'role-resource';
    return 'resource';
  };

  const handleTimelineViewChange = (value: string) => {
    updateSettings({ timelineDefaultView: value });
    // Toast removed - dropdown selection provides visual feedback
  };

  const handleTimelineTimezoneChange = async (value: string) => {
    // Set user-level timezone override
    await setUserTimezone(value);
    
    toast.success('Personal timezone updated', {
      description: 'This timezone will be used for your timeline and new bookings.',
    });
  };

  const handleResetTimezoneToDefault = async () => {
    // Clear user override and revert to system default
    await clearUserTimezone();
    
    toast.success('Timezone reset to system default', {
      description: `Now using system default: ${systemDefault}`,
    });
  };

  // Initial loading: fetch existing calendar URL on mount
  useEffect(() => {
    const fetchCalendarUrl = async () => {
      if (!user?.id) return;
      setIsLoadingCalendarUrl(true);
      try {
        const response = await calendarSyncApi.generateLink({ userId: user.id });
        const url = response.icsFileUrl;
        if (url && url.trim() !== '') {
          setCalendarUrl(url);
        } else {
          setCalendarUrl('');
        }
      } catch {
        setCalendarUrl('');
      } finally {
        setIsLoadingCalendarUrl(false);
      }
    };
    fetchCalendarUrl();
  }, [user?.id]);

  // Calendar sync handlers (moved from Integrations page)
  const handleGenerateCalendarUrl = async () => {
    setIsGeneratingUrl(true);
    try {
      const response = await calendarSyncApi.generateLink({ userId: user.id });
      const url = response.icsFileUrl;
      if (url && url.trim() !== '') {
        setCalendarUrl(url);
      } else {
        setCalendarUrl('');
      }
      toast.success('Calendar URL generated');
    } catch (error) {
      toast.error('Failed to generate calendar URL', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  const handleCopyCalendarUrl = () => {
    if (!calendarUrl) return;
    try {
      // Use a temporary textarea + execCommand as fallback for environments
      // where the Clipboard API is blocked by permissions policy (e.g. iframes)
      const textarea = document.createElement('textarea');
      textarea.value = calendarUrl;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('URL copied', { description: 'Calendar URL copied to clipboard.' });
    } catch {
      toast.error('Copy failed', { description: 'Could not copy to clipboard.' });
    }
  };

  const handleDeleteCalendarUrl = async () => {
    setIsDeletingUrl(true);
    try {
      await calendarSyncApi.deleteLink({ userId: user.id });
      setCalendarUrl('');
      setIsDeleteUrlDialogOpen(false);
      toast.success('Calendar URL deleted');
    } catch (error) {
      toast.error('Failed to delete calendar URL', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsDeletingUrl(false);
    }
  };




  // Helper function to check if a section matches the search query
  // Helper function to check if a setting matches the search query
  const matchesSearch = (keywords: string[]): boolean => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return keywords.some(keyword => keyword.toLowerCase().includes(query));
  };

  // Individual setting visibility
  const showAbsenceSetting = matchesSearch(['absence', 'show absence', 'label override']);
  const showPublicHolidaysSetting = matchesSearch(['public holiday', 'holiday', 'show public holidays']);
  const showProjectStatusesSetting = matchesSearch(['project status', 'available project status']);
  const showCostCentreStatusesSetting = matchesSearch(['cost centre status', 'available cost centre status']);
  const showStatusSettingsSetting = matchesSearch(['status settings', 'status', 'color', 'colour', 'colour override', 'color override', 'override']);
  const showUtilisationSetting = matchesSearch(['utilisation', 'utilization', 'utilisation %', 'utilization %', 'tier', 'percentage', 'heatmap', 'bands', 'utilisation bands']);
  const showBookingTypeSetupSetting = matchesSearch(['booking type', 'booking type setup', 'type setup', 'auto update timesheet', 'repeat bookings']);
  const showBusinessHoursSetting = matchesSearch(['business hours', 'working hours', 'hours']);
  const showTimezonesSetting = matchesSearch(['timezone', 'time zone', 'multiple timezone']);
  const showDefaultResourceSetting = matchesSearch(['default resource', 'resource', 'label']);



  const showCalendarSyncSetting = matchesSearch(['calendar sync', 'external calendar', 'calendar url', 'ics', 'calendar feed']);
  const showConflictDetectionSetting = matchesSearch(['conflict detection', 'conflict', 'detection']);
  const showEmailNotificationsSetting = matchesSearch(['email notification', 'email', 'notification']);
  const showAutoSyncSetting = matchesSearch(['auto sync', 'sync', 'automatic']);

  // Individual setting items for General Settings page
  const showTimeRangeItem = matchesSearch(['time range', 'day', 'week', 'month', 'quarter', 'year', 'time period', 'timeline', 'defaults']);
  const showDefaultBookingTypeItem = matchesSearch(['booking type', 'default booking type', 'timeline', 'defaults']);
  const showDefaultBookingStatusItem = matchesSearch(['booking status', 'default status', 'planned', 'confirmed', 'timeline', 'defaults']);
  const showTimelineViewItem = matchesSearch(['timeline view', 'view mode', 'customer', 'project', 'resource', 'role', 'timeline', 'defaults']);
  const showTimezoneDefaultItem = matchesSearch(['timezone default', 'timezone', 'time zone', 'defaults']);
  const showConfirmDragDropItem = matchesSearch(['confirm drag', 'drag drop', 'drag and drop', 'confirmation', 'user preferences', 'preferences', 'personal']);
  const showDefaultLoggedInResourceItem = matchesSearch(['default to logged in', 'logged in resource', 'default resource', 'user preferences', 'preferences', 'personal']);

  // Derive section-level visibility from individual items
  const showTimelineDefaultsSetting = showTimeRangeItem || showDefaultBookingTypeItem || showDefaultBookingStatusItem || showTimelineViewItem || showTimezoneDefaultItem;
  const showUserPreferencesSetting = showConfirmDragDropItem || showDefaultLoggedInResourceItem;

  // Section visibility - show section if any setting within it matches
  const showAdminSection = isAdministrator && (
    showAbsenceSetting ||
    showPublicHolidaysSetting ||
    showProjectStatusesSetting ||
    showCostCentreStatusesSetting ||
    showStatusSettingsSetting ||
    showUtilisationSetting ||
    showBusinessHoursSetting ||
    showDefaultResourceSetting ||
    searchQuery.trim() !== '' || // Always show admin section when searching
    activeTab === 'admin' // Always show admin section when on admin tab
  );

  const showCalendarSyncSection = showCalendarSyncSetting;

  const showTimelineSection = showTimelineDefaultsSetting || showUserPreferencesSetting;

  const hasResults = showAdminSection || showTimelineSection || showCalendarSyncSection;

  // Auto-switch to timezone tab when searching for timezone-related terms
  useEffect(() => {
    if (showTimezonesSetting && searchQuery.trim() !== '') {
      setActiveTab('timezone');
    }
  }, [showTimezonesSetting, searchQuery]);

  // Auto-switch to booking types tab when searching for booking type-related terms
  useEffect(() => {
    if (showBookingTypeSetupSetting && searchQuery.trim() !== '') {
      setActiveTab('booking-type-setup');
    }
  }, [showBookingTypeSetupSetting, searchQuery]);

  // Auto-expand collapsible sections in General Settings when search matches items inside them
  useEffect(() => {
    if (!searchQuery.trim()) return;
    if (showTimelineDefaultsSetting) {
      setOpenSettingsSection('defaults');
    } else if (showUserPreferencesSetting) {
      setOpenSettingsSection('preferences');
    } else if (showCalendarSyncSetting) {
      setOpenSettingsSection('calendarSync');
    }
  }, [searchQuery, showTimelineDefaultsSetting, showUserPreferencesSetting, showCalendarSyncSetting]);

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
    <div className="w-full px-4 py-6 pb-24 space-y-6">
      {activeTab !== 'settings' && (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              {activeTab === 'admin'
                ? <Shield className="h-4 w-4 text-primary" />
                : activeTab === 'timezone'
                ? <Globe className="h-4 w-4 text-primary" />
                : activeTab === 'booking-type-setup'
                ? <Zap className="h-4 w-4 text-primary" />
                : activeTab === 'group-setup'
                ? <LayoutGrid className="h-4 w-4 text-primary" />
                : activeTab === 'integrations'
                ? <Plug className="h-4 w-4 text-primary" />
                : activeTab === 'audit'
                ? <ShieldCheck className="h-4 w-4 text-primary" />
                : <SettingsIcon className="h-4 w-4 text-primary" />
              }
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">
                {activeTab === 'admin' ? 'Administrator Settings' 
                 : activeTab === 'timezone' ? 'Timezone Settings'
                 : activeTab === 'booking-type-setup' ? 'Booking Type Setup'
                 : activeTab === 'group-setup' ? 'Planner Group Setup'
                 : activeTab === 'integrations' ? 'Integration Settings'
                 : activeTab === 'audit' ? 'Audit Settings'
                 : 'General Settings'}
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {activeTab === 'admin' ? 'Manage roles, permissions and user access'
                 : activeTab === 'timezone' ? 'Configure timezone support for your organisation'
                 : activeTab === 'booking-type-setup' ? 'Define and customise booking types'
                 : activeTab === 'group-setup' ? 'Organise resources into planner groups'
                 : activeTab === 'integrations' ? 'Connect to external systems and services'
                 : activeTab === 'audit' ? 'Control which application areas are audited'
                 : 'Manage your general application preferences'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DataModeToggle screen="settings" />
            <HelpButton />
          </div>
        </div>
      )}

      {/* Top-level tab navigation removed — now handled by sidebar menu */}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (!canViewIntegrations() ? <AccessDenied /> : <Integrations />)}

      {/* Timezone Settings Tab */}
      {activeTab === 'timezone' && (!canViewSettingsTab('viewTimezone') ? <AccessDenied /> : (
        <div className="space-y-6 pb-24">
          <Card className="border-primary/20">
            <CardContent className="space-y-6 pt-6">
              <Accordion type="multiple" defaultValue={["timezoneSettings"]} className="space-y-4">
                <AccordionItem value="timezoneSettings" className="border rounded-lg">
                  <AccordionTrigger className="hover:no-underline px-4">
                    <div className="font-semibold">Configuration</div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4 px-4">
                    <div className="space-y-4">
                      {/* Feature Toggle */}
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                        <div className="flex-1 min-w-0">
                          <Label htmlFor="enable-timezones" className="text-sm font-medium cursor-pointer">
                            Enable Multiple Timezones
                          </Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Multiple timezones allow users to view and work with schedules across different time zones.
                          </p>
                        </div>
                        <Checkbox
                          id="enable-timezones"
                          checked={tzView.enableTimezones}
                          onCheckedChange={(v) => handleTimezonesToggle(!!v)}
                        />
                      </div>

                      {!tzView.enableTimezones && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-sm text-blue-900 dark:text-blue-100">
                            <strong>Default Timezone:</strong> Europe/London (UK)
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            When multiple timezones are disabled, the system operates using UK timezone exclusively.
                          </p>
                        </div>
                      )}

                      <div className={`space-y-4 ${!tzView.enableTimezones ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Available Timezones */}
                        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                          <div className="flex-1 min-w-0">
                            <Label htmlFor="available-timezones" className="text-sm font-medium">Available Timezones</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">Select which timezones will be available to users throughout the application.</p>
                            <div className="mt-2">
                              <MultiSelect
                                options={timezoneOptions}
                                placeholder="Select timezones..."
                                emptyMessage="No timezones found."
                                maxDisplay={2}
                                value={tzView.availableTimezones}
                                onValueChange={handleTimezonesChange}
                              />
                            </div>
                          </div>
                        </div>

                        {/* System Default Timezone */}
                        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                          <div className="flex-1 min-w-0">
                            <Label htmlFor="default-timezone" className="text-sm font-medium">System Default Timezone</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">The default timezone used throughout the system for all users.</p>
                            {currentMode === 'demo' && (
                              <p className="text-xs text-amber-600 font-medium mt-1">
                                Demo Mode: Changes won&apos;t persist to database. Switch to Live mode to save permanently.
                              </p>
                            )}
                            {import.meta.env.DEV && (
                              <p className="text-xs text-muted-foreground font-mono mt-1">
                                Debug: mode={currentMode} | saved={tzView.defaultTimezone} | available={tzView.availableTimezones.length} timezones
                              </p>
                            )}
                          </div>
                          <Select
                            value={tzView.defaultTimezone}
                            onValueChange={handleDefaultTimezoneChange}
                          >
                            <SelectTrigger id="default-timezone" className="w-[240px]">
                              <SelectValue placeholder="Select default timezone" />
                            </SelectTrigger>
                            <SelectContent>
                              {TIMEZONES
                                .filter((tz) => tzView.availableTimezones.includes(tz.value))
                                .map((tz) => (
                                  <SelectItem key={tz.value} value={tz.value}>
                                    {tz.label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {!embeddedMode && (
            <SettingsSaveBar
              isDirty={timezoneIsDirty}
              isSaving={isSavingTimezone}
              onSave={handleSaveTimezoneSettings}
              onDiscard={handleDiscardTimezoneSettings}
              sidebarLeft={sidebarLeft}
            />
          )}
        </div>
      ))}

      {/* Booking Type Setup Tab */}
      {activeTab === 'booking-type-setup' && (!canViewSettingsTab('viewBookingTypeSetup') ? <AccessDenied /> : (
        <div className="space-y-6">
          <Card className="border-primary/20 relative">
            <CardContent className="pt-6">
              {currentMode !== 'demo' && isContextLoading && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center rounded-lg">
                  <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm font-medium">Loading Booking Types...</span>
                  </div>
                </div>
              )}
              <BookingTypeV2
                bookingTypes={migratedBookingTypes as any}
                bookings={bookings}
                resources={resources.filter(r => String(r.id) !== 'unallocated')}
                onSave={async (bookingType) => {
                  await updateSettings({ bookingTypes: [bookingType] as any });
                }}
                onDelete={(id) => {
                  if (migratedBookingTypes.length <= 1) {
                    toast.error('Cannot delete the last booking type');
                    return;
                  }
                  const deletedBt = migratedBookingTypes.find(bt => String(bt.id) === String(id));
                  const updatedBookingTypes = migratedBookingTypes.filter(bt => String(bt.id) !== String(id)) as any;
                  updateSettings({ bookingTypes: updatedBookingTypes });
                  audit.log({
                    user: 'Current User',
                    category: 'Settings – Booking Types',
                    action: 'Delete',
                    entity: deletedBt?.name ?? id,
                    changes: [{ field: 'Booking Type', before: deletedBt?.name ?? id, after: null }],
                  });
                }}
                dataMode={currentMode}
                demoBookingTypeUsages={demoBookingTypeUsages}
                onDemoUsageChange={(bookingTypeId, resourceId, level) => {
                  setDemoBookingTypeUsages(prev => {
                    const filtered = prev.filter(u => !(String(u.bookingTypeId) === bookingTypeId && String(u.resourceId) === resourceId));
                    if (level === 'none') return filtered;
                    return [...filtered, {
                      id: Date.now(),
                      resourceId: resourceId as unknown as number,
                      bookingTypeId: bookingTypeId as unknown as number,
                      isReadOnly: level === 'read-only',
                      isFullAccess: level === 'full-access',
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    }];
                  });
                }}
                onDemoBulkUsageChange={(bookingTypeId, resourceIds, level) => {
                  setDemoBookingTypeUsages(prev => {
                    const filtered = prev.filter(u => !(String(u.bookingTypeId) === bookingTypeId && resourceIds.includes(String(u.resourceId))));
                    if (level === 'none') return filtered;
                    return [...filtered, ...resourceIds.map((rid, idx) => ({
                      id: Date.now() + idx,
                      resourceId: rid as unknown as number,
                      bookingTypeId: bookingTypeId as unknown as number,
                      isReadOnly: level === 'read-only',
                      isFullAccess: level === 'full-access',
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    }))];
                  });
                }}
              />
            </CardContent>
          </Card>
        </div>
      ))}

      {/* Group Setup Tab */}
      {activeTab === 'group-setup' && (!canViewSettingsTab('viewGroupSetup') ? <AccessDenied /> : (
        <div className="space-y-6">
          {settings.groupingMethod === 'scheduler-group' ? (
            <GroupSetupTab 
              currentMode={currentMode}
              schedulerGroups={schedulerGroups}
              resources={resources}
              addSchedulerGroup={async (group) => {
                await contextAddSchedulerGroup(group, currentMode, true);
                audit.log({
                  user: 'Current User',
                  category: 'Settings – Group Setup',
                  action: 'New',
                  entity: group.name ?? 'New Group',
                  changes: [{ field: 'Group', before: null, after: group.name ?? 'New Group' }],
                });
              }}
              updateSchedulerGroup={async (id, updates) => {
                await updateSchedulerGroup(id, updates);
              }}
              saveSchedulerGroup={async (id) => {
                const group = schedulerGroups.find((g: { id: string | number }) => String(g.id) === String(id));
                await saveSchedulerGroup(id);
                audit.log({
                  user: 'Current User',
                  category: 'Settings – Group Setup',
                  action: 'Edit',
                  entity: group?.name ?? id,
                  changes: [{ field: 'Group', before: null, after: 'Saved' }],
                });
              }}
              deleteSchedulerGroup={async (id) => {
                const group = schedulerGroups.find((g: { id: string | number }) => String(g.id) === String(id));
                await deleteSchedulerGroup(id);
                audit.log({
                  user: 'Current User',
                  category: 'Settings – Group Setup',
                  action: 'Delete',
                  entity: group?.name ?? id,
                  changes: [{ field: 'Group', before: group?.name ?? id, after: null }],
                });
              }}
            />
          ) : (
            <Card className="border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  Group Setup is only available when the grouping method is set to &quot;Planner Group&quot;. 
                  You can change this in Administrator Settings &gt; General &gt; Resource Grouping.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ))}

      {/* Audit Settings Tab */}
      {activeTab === 'audit' && (
        <AuditSettings onNavigate={onNavigate} />
      )}

      {/* General Settings — Tabbed Interface */}
      {activeTab === 'settings' && (!canViewSettingsTab('viewGeneral') ? <AccessDenied /> : (
        <div className="space-y-6 pb-24">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <SettingsIcon className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold leading-tight">General Settings</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DataModeToggle screen="settings" />
            </div>
          </div>

          {/* Tabbed Interface */}
          <Tabs value={generalSubTab} onValueChange={setGeneralSubTab} className="space-y-6">
            <TabsList variant="icon" showArrows className="w-full sticky top-0 z-10 bg-background border-b border-border/60 rounded-none px-0 shadow-sm">
              <TabsTrigger value="heatmap-settings" variant="icon" showUnderline className="flex-1">
                <BarChart3 className="h-4 w-4 shrink-0" />
                <span className="truncate">Heatmap Settings</span>
                {generalTabsWithChanges.has('heatmap-settings') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>
              <TabsTrigger value="planning" variant="icon" showUnderline className="flex-1">
                <Calendar className="h-4 w-4 shrink-0" />
                <span className="truncate">Planning</span>
                {generalTabsWithChanges.has('planning') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>
              {!hiddenTabs.includes('resource-rates') && (
                <TabsTrigger value="resource-rates" variant="icon" showUnderline className="flex-1">
                  <span className="truncate">Resource Rates</span>
                  {generalTabsWithChanges.has('resource-rates') && (
                    <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="match-thresholds" variant="icon" showUnderline className="flex-1">
                <Zap className="h-4 w-4 shrink-0" />
                <span className="truncate">Match Thresholds</span>
                {generalTabsWithChanges.has('match-thresholds') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>

              <TabsTrigger value="defaults" variant="icon" showUnderline className="flex-1">
                <Settings2 className="h-4 w-4 shrink-0" />
                <span className="truncate">Defaults</span>
                {generalTabsWithChanges.has('defaults') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>
              <TabsTrigger value="costing-statuses" variant="icon" showUnderline className="flex-1">
                <span className="truncate">Costing Statuses</span>
                {generalTabsWithChanges.has('costing-statuses') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>
              <TabsTrigger value="status-settings" variant="icon" showUnderline className="flex-1">
                <Palette className="h-4 w-4 shrink-0" />
                <span className="truncate">Status Settings</span>
                {generalTabsWithChanges.has('status-settings') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>
              {!hiddenTabs.includes('absence-holidays') && (
                <TabsTrigger value="absence-holidays" variant="icon" showUnderline className="flex-1">
                  <CalendarClock className="h-4 w-4 shrink-0" />
                  <span className="truncate">Absence & Holidays</span>
                  {generalTabsWithChanges.has('absence-holidays') && (
                    <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="open-demand" variant="icon" showUnderline className="flex-1">
                <Filter className="h-4 w-4 shrink-0" />
                <span className="truncate">Open Demand</span>
                {generalTabsWithChanges.has('open-demand') && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" aria-label="Unsaved changes" />
                )}
              </TabsTrigger>
            </TabsList>

            {/* Heatmap Settings Tab */}
            <TabsContent value="heatmap-settings" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <Accordion type="multiple" defaultValue={["utilisationBands"]} className="space-y-4">

                    {/* Utilisation Bands */}
                    <AccordionItem value="utilisationBands" className="border rounded-lg">
                      <AccordionTrigger className="hover:no-underline px-4">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-primary/10 p-2">
                            <BarChart3 className="h-4 w-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">Utilisation Bands</div>
                            <div className="text-sm text-muted-foreground font-normal">Define colour-coded capacity tiers for the heatmap</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4 px-4">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Threshold boundaries</Label>
                            <p className="text-xs text-muted-foreground">Drag the handles to move the boundary between adjacent bands.</p>
                            <UtilisationSlider tiers={utilisationTiers} onBoundaryChange={handleUtilisationBoundaryChange} />
                          </div>

                          <div className="space-y-3">
                            <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center font-medium text-sm text-muted-foreground pb-2">
                              <div>On</div>
                              <div>Band label</div>
                              <div>Colour</div>
                            </div>

                            {(() => {
                              const descriptors: Record<string, string> = {
                                'tier-1': 'Resource has unallocated capacity.',
                                'tier-2': 'Within target utilisation range.',
                                'tier-3': 'Approaching full allocation — review before adding bookings.',
                                'tier-4': 'Bookings exceed available capacity.',
                              };
                              return utilisationTiers.filter((tier) => tier.label !== 'No bookings').map((tier) => (
                                <div key={tier.id} className="space-y-1">
                                  <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
                                    <Checkbox
                                      checked={tier.enabled}
                                      onCheckedChange={(checked) => handleUtilisationTierToggle(tier.id, !!checked)}
                                    />
                                    <Input
                                      value={tier.customLabel || ''}
                                      onChange={(e) => handleUtilisationTierLabelChange(tier.id, e.target.value)}
                                      onBlur={() => handleUtilisationTierLabelBlur(tier.id)}
                                      placeholder={tier.label}
                                      className="h-8 text-sm"
                                      disabled={!tier.enabled}
                                    />
                                    <ColorPicker
                                      value={tier.color}
                                      onChange={(color) => handleUtilisationTierColorChange(tier.id, color)}
                                      disabled={!tier.enabled}
                                    />
                                  </div>
                                  {descriptors[tier.id] && (
                                    <p className="text-xs text-muted-foreground pl-[calc(theme(spacing.9)+theme(spacing.3))]">
                                      {descriptors[tier.id]}
                                    </p>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>

                          <div className="flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleResetUtilisationTiers}
                              className="shrink-0 text-xs"
                            >
                              Reset to defaults
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Forecast View Settings */}
                    <AccordionItem value="forecastSettings" className="border rounded-lg">
                      <AccordionTrigger className="hover:no-underline px-4">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-primary/10 p-2">
                            <CalendarClock className="h-4 w-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">Forecast View Settings</div>
                            <div className="text-sm text-muted-foreground font-normal">Control how planned bookings are signalled on heatmap views</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4 px-4">
                        <div className="space-y-6">
                          <div className="space-y-1">
                            <h4 className="text-sm font-semibold">Planned-Only Warning Thresholds</h4>
                            <p className="text-xs text-muted-foreground">
                              When a heatmap cell contains <em>only</em> planned (unconfirmed) bookings, the cell
                              receives a dashed border and a ◈ prefix. Use the sliders below to set the
                              utilisation threshold at which this warning appears for each time granularity.
                            </p>
                          </div>

                          {(() => {
                            const DEFAULT_FS = { monthThreshold: 70 as number, quarterThreshold: 60 as number, yearThreshold: 50 as number, showPlannedPrefix: true as boolean, showConfirmedPlannedSplit: true as boolean };
                            const fs = { ...DEFAULT_FS, ...settings.forecastSettings };
                            const patchFs = (patch: Partial<typeof DEFAULT_FS>) => updateSettings({ forecastSettings: { ...fs, ...patch } });
                            return (
                              <>
                                {/* Month */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                      <Label className="text-sm font-medium">Month view threshold</Label>
                                      <p className="text-xs text-muted-foreground">Short-term — relatively certain. Default 70%.</p>
                                    </div>
                                    <span className="text-sm font-semibold tabular-nums text-primary min-w-[3ch] text-right">{fs.monthThreshold}%</span>
                                  </div>
                                  <input type="range" min={40} max={100} step={5} value={fs.monthThreshold}
                                    onChange={(e) => patchFs({ monthThreshold: parseInt(e.target.value) })}
                                    className="w-full accent-primary" />
                                  <div className="flex justify-between text-xs text-muted-foreground"><span>60% — flag earlier</span><span>100% — only when overloaded</span></div>
                                </div>

                                {/* Quarter */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                      <Label className="text-sm font-medium">Quarter view threshold</Label>
                                      <p className="text-xs text-muted-foreground">Medium-term — moderate uncertainty. Default 60%.</p>
                                    </div>
                                    <span className="text-sm font-semibold tabular-nums text-primary min-w-[3ch] text-right">{fs.quarterThreshold}%</span>
                                  </div>
                                  <input type="range" min={40} max={100} step={5} value={fs.quarterThreshold}
                                    onChange={(e) => patchFs({ quarterThreshold: parseInt(e.target.value) })}
                                    className="w-full accent-primary" />
                                  <div className="flex justify-between text-xs text-muted-foreground"><span>60% — flag earlier</span><span>100% — only when overloaded</span></div>
                                </div>

                                {/* Year */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                      <Label className="text-sm font-medium">Year view threshold</Label>
                                      <p className="text-xs text-muted-foreground">Long-horizon — highly speculative. Flag demand earlier. Default 50%.</p>
                                    </div>
                                    <span className="text-sm font-semibold tabular-nums text-primary min-w-[3ch] text-right">{fs.yearThreshold}%</span>
                                  </div>
                                  <input type="range" min={40} max={100} step={5} value={fs.yearThreshold}
                                    onChange={(e) => patchFs({ yearThreshold: parseInt(e.target.value) })}
                                    className="w-full accent-primary" />
                                  <div className="flex justify-between text-xs text-muted-foreground"><span>60% — flag earlier</span><span>100% — only when overloaded</span></div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Display Options */}
                    <AccordionItem value="displayOptions" className="border rounded-lg">
                      <AccordionTrigger className="hover:no-underline px-4">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-primary/10 p-2">
                            <Eye className="h-4 w-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">Display Options</div>
                            <div className="text-sm text-muted-foreground font-normal">Planned booking indicators across all heatmap views</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4 px-4">
                        {(() => {
                          const DEFAULT_FS = { monthThreshold: 70 as number, quarterThreshold: 60 as number, yearThreshold: 50 as number, showPlannedPrefix: true as boolean, showConfirmedPlannedSplit: true as boolean };
                          const fs = { ...DEFAULT_FS, ...settings.forecastSettings };
                          const patchFs = (patch: Partial<typeof DEFAULT_FS>) => updateSettings({ forecastSettings: { ...fs, ...patch } });
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                                <div className="flex-1 min-w-0">
                                  <Label htmlFor="show-planned-prefix" className="text-sm font-medium cursor-pointer">Highlight planned-only cells</Label>
                                  <p className="text-xs text-muted-foreground mt-0.5">Mutes planned-only heatmap cells and gives them a dashed border so they read as unconfirmed data.</p>
                                </div>
                                <Checkbox id="show-planned-prefix" checked={fs.showPlannedPrefix}
                                  onCheckedChange={(v) => patchFs({ showPlannedPrefix: !!v })} />
                              </div>

                              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                                <div className="flex-1 min-w-0">
                                  <Label htmlFor="show-split" className="text-sm font-medium cursor-pointer">Confirmed / planned split in tooltip</Label>
                                  <p className="text-xs text-muted-foreground mt-0.5">Shows the confirmed vs. planned hour breakdown in the hover tooltip.</p>
                                </div>
                                <Checkbox id="show-split" checked={fs.showConfirmedPlannedSplit}
                                  onCheckedChange={(v) => patchFs({ showConfirmedPlannedSplit: !!v })} />
                              </div>

                              <div className="flex items-center justify-end lg:col-span-2">
                                <Button variant="outline" size="sm" className="shrink-0 text-xs"
                                  onClick={() => updateSettings({ forecastSettings: { monthThreshold: 70, quarterThreshold: 60, yearThreshold: 50, showPlannedPrefix: true, showConfirmedPlannedSplit: true } })}>
                                  Reset to defaults
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                      </AccordionContent>
                    </AccordionItem>

                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Planning Tab */}
            <TabsContent value="planning" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <Accordion type="multiple" defaultValue={["capacityRisk"]} className="space-y-4">

                    {/* Capacity Risk & Conflict Resolution */}
                    <AccordionItem value="capacityRisk" className="border rounded-lg">
                      <AccordionTrigger className="hover:no-underline px-4">
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-primary/10 p-2">
                            <Zap className="h-4 w-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">Capacity Risk & Conflict Resolution</div>
                            <div className="text-sm text-muted-foreground font-normal">Monitor utilisation and resolve booking conflicts</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4 px-4">
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            When enabled, the scheduler monitors resource utilisation, surfaces at-risk
                            bookings in the heatmap view, and provides a What-if Analysis overlay to help
                            resolve conflicts before they impact delivery.
                          </p>

                          {/* Master toggle */}
                          <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                            <div className="flex-1 min-w-0">
                              <Label htmlFor="what-if-master-general" className="text-sm font-medium cursor-pointer">
                                Enable capacity risk monitoring
                              </Label>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {effectiveWhatIfEnabled
                                  ? 'Burnout risk alerts and the What-if Analysis overlay are active.'
                                  : 'All capacity risk features are hidden. Planners see the scheduler only.'}
                              </p>
                            </div>
                            <Checkbox
                              id="what-if-master-general"
                              checked={effectiveWhatIfEnabled}
                              onCheckedChange={() => handleToggleWhatIf()}
                            />
                          </div>

                          {/* Resolution strategies */}
                          {effectiveWhatIfEnabled && (
                            <>
                              <div className="flex items-center gap-2 px-1 pt-2 pb-0.5">
                                <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Resolution Strategies</span>
                              </div>
                              <p className="text-xs text-muted-foreground px-1">
                                Choose which options appear in the What-if Analysis overlay when a conflict
                                is detected. At least one must remain enabled.
                              </p>

                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {RESOLUTION_OPTION_CONFIGS.map((opt) => {
                                  const on = effectiveIsResolutionEnabled(opt.id);
                                  return (
                                    <div
                                      key={opt.id}
                                      className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background"
                                    >
                                      <div className="flex-1 min-w-0">
                                        <Label
                                          htmlFor={`resolution-general-${opt.id}`}
                                          className={`text-sm font-medium cursor-pointer ${
                                            on ? 'text-foreground' : 'text-muted-foreground'
                                          }`}
                                        >
                                          {opt.label}
                                        </Label>
                                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                          {opt.guidance}
                                        </p>
                                      </div>
                                      <Checkbox
                                        id={`resolution-general-${opt.id}`}
                                        checked={on}
                                        onCheckedChange={() => handleToggleResolutionOption(opt.id)}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Resource Rates Tab */}
            <TabsContent value="resource-rates" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Show Resource Rates */}
                    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                      <div className="flex-1 min-w-0">
                        <Label htmlFor="show-rates-general" className="text-sm font-medium cursor-pointer">Show Resource Rates</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Display resource rates in the application</p>
                      </div>
                      <Checkbox id="show-rates-general" checked={settings.showResourceRates} onCheckedChange={(v) => handleShowResourceRatesToggle(!!v)} />
                    </div>
                    {/* Copy Resource Rates */}
                    {settings.showResourceRates && (
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                        <div className="flex-1 min-w-0">
                          <Label htmlFor="copy-rates-general" className="text-sm font-medium cursor-pointer">Copy Resource Rates</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Include resource rates when copying or duplicating bookings</p>
                        </div>
                        <Checkbox id="copy-rates-general" checked={settings.copyResourceRates} onCheckedChange={(v) => handleCopyResourceRatesToggle(!!v)} />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Match Score Thresholds Tab */}
            <TabsContent value="match-thresholds" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <div className="space-y-4">
                    {/* RAG preview */}
                    <div className="flex items-center gap-3 rounded-lg border px-4 py-3 bg-muted/40">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        <span className="text-sm font-medium text-emerald-700">
                          Green ≥ {settings.aiRagThresholds?.greenThreshold ?? 80}%
                        </span>
                      </div>
                      <span className="text-muted-foreground">·</span>
                      <div className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                        <span className="text-sm font-medium text-amber-700">
                          Amber ≥ {settings.aiRagThresholds?.amberThreshold ?? 50}%
                        </span>
                      </div>
                      <span className="text-muted-foreground">·</span>
                      <div className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
                        <span className="text-sm font-medium text-destructive">
                          Red &lt; {settings.aiRagThresholds?.amberThreshold ?? 50}%
                        </span>
                      </div>
                    </div>

                    {/* Threshold inputs */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="rag-green-general" className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" />
                          Green threshold (%)
                        </Label>
                        <Input
                          id="rag-green-general"
                          type="number"
                          min={1}
                          max={100}
                          value={settings.aiRagThresholds?.greenThreshold ?? 80}
                          onChange={e => handleRagThresholdChange('greenThreshold', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Scores at or above this value are shown in green</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rag-amber-general" className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-amber-400 inline-block" />
                          Amber threshold (%)
                        </Label>
                        <Input
                          id="rag-amber-general"
                          type="number"
                          min={1}
                          max={100}
                          value={settings.aiRagThresholds?.amberThreshold ?? 50}
                          onChange={e => handleRagThresholdChange('amberThreshold', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Scores at or above this value (but below green) are shown in amber. Below this is red</p>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={handleResetRagThresholds} className="gap-2">
                        <RotateCcw className="h-3.5 w-3.5" />
                        Reset to defaults (80 / 50)
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>



            {/* Defaults Tab */}
            <TabsContent value="defaults" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <div className="space-y-4">
                    {/* Business Hours Settings */}
                    {showBusinessHoursSetting && (
                      <>
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 px-1 pt-1 pb-0.5">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business Hours</span>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {/* Start Time */}
                            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                              <div className="flex-1 min-w-0">
                                <Label htmlFor="business-hours-start-general" className="text-sm font-medium">Start Time</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">When business hours begin (24-hour format)</p>
                              </div>
                              <Input
                                id="business-hours-start-general"
                                type="time"
                                value={localBusinessHoursStart}
                                onChange={(e) => handleBusinessHoursStartChange(e.target.value)}
                                onBlur={handleBusinessHoursStartBlur}
                                className="w-[140px]"
                              />
                            </div>
                            {/* End Time */}
                            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                              <div className="flex-1 min-w-0">
                                <Label htmlFor="business-hours-end-general" className="text-sm font-medium">End Time</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">When business hours end (24-hour format)</p>
                              </div>
                              <Input
                                id="business-hours-end-general"
                                type="time"
                                value={localBusinessHoursEnd}
                                onChange={(e) => handleBusinessHoursEndChange(e.target.value)}
                                onBlur={handleBusinessHoursEndBlur}
                                className="w-[140px]"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Visible Hours */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 px-1 pt-2 pb-0.5">
                            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Visible Hours</span>
                          </div>
                          <p className="text-xs text-muted-foreground px-1">
                            The time range shown in Day and Week calendar views. Set a buffer either side of business hours so staff can see early starts and late finishes.
                          </p>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                              <div className="flex-1 min-w-0">
                                <Label htmlFor="visible-hours-start-general" className="text-sm font-medium">Visible From</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">Earliest visible time slot (default 07:00)</p>
                              </div>
                              <Input
                                id="visible-hours-start-general"
                                type="time"
                                value={localVisibleHoursStart}
                                onChange={(e) => handleVisibleHoursStartChange(e.target.value)}
                                onBlur={handleVisibleHoursStartBlur}
                                className="w-[140px]"
                              />
                            </div>
                            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                              <div className="flex-1 min-w-0">
                                <Label htmlFor="visible-hours-end-general" className="text-sm font-medium">Visible Until</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">Latest visible time slot (default 19:00)</p>
                              </div>
                              <Input
                                id="visible-hours-end-general"
                                type="time"
                                value={localVisibleHoursEnd}
                                onChange={(e) => handleVisibleHoursEndChange(e.target.value)}
                                onBlur={handleVisibleHoursEndBlur}
                                className="w-[140px]"
                              />
                            </div>
                          </div>
                        </div>

                        <Separator />
                      </>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {/* Quick Add Default */}
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background lg:col-span-2">
                        <div className="flex-1 min-w-0">
                          <Label htmlFor="quick-add-default-general" className="text-sm font-medium">Quick Add Default</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Default booking type for quick add functionality</p>
                        </div>
                        <Select
                          value={settings.quickAddDefault ? String(settings.quickAddDefault) : undefined}
                          onValueChange={handleQuickAddDefaultChange}
                        >
                          <SelectTrigger id="quick-add-default-general" className="w-[200px]">
                            <SelectValue placeholder="Select default booking type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {settings.bookingTypes.map((bookingType) => (
                              <SelectItem key={bookingType.id} value={String(bookingType.id)}>
                                {bookingType.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Description Default */}
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background lg:col-span-2">
                        <div className="flex-1 min-w-0">
                          <Label htmlFor="description-default-general" className="text-sm font-medium">Description Default</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Default source for booking descriptions</p>
                        </div>
                        <Select
                          value={settings.descriptionDefault}
                          onValueChange={handleDescriptionDefaultChange}
                        >
                          <SelectTrigger id="description-default-general" className="w-[200px]">
                            <SelectValue placeholder="Select description default" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="from-project">From Project</SelectItem>
                            <SelectItem value="from-project-description">From Project Description</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Grouping Method */}
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background lg:col-span-2">
                        <div className="flex-1 min-w-0">
                          <Label htmlFor="grouping-method-general" className="text-sm font-medium">Grouping Method</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Choose how resources are grouped in the calendar view</p>
                        </div>
                        <Select
                          value={settings.groupingMethod}
                          onValueChange={handleGroupingMethodChange}
                        >
                          <SelectTrigger id="grouping-method-general" className="w-[200px]">
                            <SelectValue placeholder="Select grouping method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduler-group">Planner Group</SelectItem>
                            <SelectItem value="department">Department</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Available Costing Statuses Tab */}
            <TabsContent value="costing-statuses" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <div className="space-y-4">
                    {/* Project Status Settings */}
                    {showProjectStatusesSetting && (
                      <>
                        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                              <Label className="text-sm font-medium">Available Project Statuses</Label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">Select which project statuses should be available in the system</p>
                            <div className="mt-2">
                              <MultiSelect
                                options={[{ value: 'all', label: 'All' }, ...PROJECT_STATUSES]}
                                value={settings.availableProjectStatuses === 'all' ? ['all', ...allProjectStatusValues] : settings.availableProjectStatuses}
                                onValueChange={handleProjectStatusesChange}
                                placeholder="Select project statuses"
                                maxDisplay={2}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Cost Centre Status Settings */}
                    {showCostCentreStatusesSetting && (
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                            <Label className="text-sm font-medium">Available Cost Centre Statuses</Label>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">Select which cost centre statuses should be available in the system</p>
                          <div className="mt-2">
                            <MultiSelect
                              options={[{ value: 'all', label: 'All' }, ...PROJECT_STATUSES]}
                              value={settings.availableCostCentreStatuses === 'all' ? ['all', ...allProjectStatusValues] : settings.availableCostCentreStatuses}
                              onValueChange={handleCostCentreStatusesChange}
                              placeholder="Select cost centre statuses"
                              maxDisplay={2}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Status Settings Tab */}
            <TabsContent value="status-settings" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  {showStatusSettingsSetting && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Configure the text and color for each booking status
                        </p>
                        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
                          <div className="flex-shrink-0 mt-0.5">
                            <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="text-xs text-blue-900 dark:text-blue-100">
                            <strong>Retroactive Updates:</strong> When you change a status color, all existing bookings with that status will automatically update to display the new color across all views in real-time.
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center font-medium text-sm text-muted-foreground pb-2">
                          <div></div>
                          <div>Status Text</div>
                          <div>Colour</div>
                        </div>

                        {settings.statusConfigs.map((status) => {
                          const STATUS_DESCRIPTIONS: Record<string, { description: string; heatmapNote?: string }> = {
                            planned: {
                              description: 'Work that is tentatively scheduled but not yet confirmed. Bookings can still be moved or cancelled.',
                              heatmapNote: 'Heatmap cells show a dashed border + ◈ prefix when all bookings are Planned (or other non-confirmed statuses).',
                            },
                            confirmed: {
                              description: 'Work that has been reviewed and locked in. Confirmed bookings count as firm capacity in heatmap utilisation calculations.',
                              heatmapNote: 'Confirmed bookings always count towards solid (non-dashed) cell rendering on the heatmap.',
                            },
                            cancelled: {
                              description: 'Work that was planned or confirmed but has since been called off. Cancelled bookings are excluded from utilisation calculations.',
                              heatmapNote: 'Cancelled bookings are treated as non-confirmed on the heatmap — a cell with only Cancelled bookings shows the dashed border treatment.',
                            },
                            completed: {
                              description: 'Work that has been carried out. Completed bookings count as firm capacity, the same as Confirmed.',
                              heatmapNote: 'Completed bookings always count towards solid (non-dashed) cell rendering on the heatmap.',
                            },
                          };
                          const meta = STATUS_DESCRIPTIONS[String(status.id)];

                          return (
                            <div key={status.id} className="space-y-1.5">
                              <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
                                <Checkbox
                                  id={`status-general-${status.id}`}
                                  checked={status.enabled}
                                  onCheckedChange={(checked) =>
                                    handleStatusEnabledChange(status.id, checked as boolean)
                                  }
                                />
                                <Input
                                  value={status.customText ?? ''}
                                  onChange={(e) => handleStatusCustomTextChange(status.id, e.target.value)}
                                  onBlur={() => handleStatusCustomTextBlur(status.id)}
                                  onFocus={(e) => e.target.setSelectionRange(0, 0)}
                                  placeholder={status.text}
                                  className="h-8 text-sm"
                                  disabled={!status.enabled}
                                />
                                <ColorPicker
                                  value={status.color}
                                  onChange={(color) => handleStatusColorChange(status.id, color)}
                                  disabled={!status.enabled}
                                />
                              </div>
                              {meta && (
                                <div className="pl-7 space-y-0.5">
                                  <p className="text-xs text-muted-foreground leading-relaxed">
                                    {meta.description}
                                  </p>
                                  {meta.heatmapNote && (
                                    <p className="text-xs text-muted-foreground/70 leading-relaxed italic">
                                      {meta.heatmapNote}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <Separator className="my-6" />

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-sm">Colour Overrides</h4>
                          <p className="text-sm text-muted-foreground">
                            Active colour overrides (marked with a tick) appear as selectable options in the booking form's colour dropdown when the colour field is enabled. Selecting an override replaces the default status colour when viewing bookings on the timeline.
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center font-medium text-sm text-muted-foreground pb-2">
                            <div></div>
                            <div>Override Text</div>
                            <div>Colour</div>
                            <div></div>
                          </div>

                          {settings.colorOverrides.map((override) => (
                            <div key={override.id} className="grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center">
                              <Checkbox
                                id={`override-general-${override.id}`}
                                checked={override.enabled}
                                onCheckedChange={(checked) => 
                                  handleColorOverrideEnabledChange(override.id, checked as boolean)
                                }
                              />
                              <Input
                                value={override.customText || ''}
                                onChange={(e) => handleColorOverrideCustomTextChange(override.id, e.target.value)}
                                onBlur={() => handleColorOverrideCustomTextBlur(override.id)}
                                placeholder={override.text}
                                className="h-8 text-sm"
                                disabled={!override.enabled}
                              />
                              <ColorPicker
                                value={override.color}
                                onChange={(color) => handleColorOverrideColorChange(override.id, color)}
                                disabled={!override.enabled}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteColorOverride(override.id)}
                                className="h-9 w-9"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}

                          <Button
                            onClick={handleAddColorOverride}
                            variant="outline"
                            className="w-full mt-2"
                          >
                            Add Override
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Absence & Holiday Settings Tab */}
            <TabsContent value="absence-holidays" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-6 pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Absence Color */}
                    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                      <div className="flex-1 min-w-0">
                        <Label htmlFor="absence-color-general" className="text-sm font-medium">Absence Color</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Choose the display color for absence in the calendar</p>
                      </div>
                      <ColorPicker
                        value={settings.absenceColor}
                        onChange={handleAbsenceColorChange}
                      />
                    </div>

                    {/* Public Holiday Color */}
                    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                      <div className="flex-1 min-w-0">
                        <Label htmlFor="holiday-color-general" className="text-sm font-medium">Public Holiday Color</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Choose the display color for public holidays in the calendar</p>
                      </div>
                      <ColorPicker
                        value={settings.publicHolidayColor}
                        onChange={handlePublicHolidayColorChange}
                      />
                    </div>

                    {/* Absence Label Override */}
                    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background lg:col-span-2">
                      <div className="flex-1 min-w-0">
                        <Label htmlFor="absence-label-general" className="text-sm font-medium">Absence Label Override</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Leave empty to use the default &quot;Absence&quot; label</p>
                      </div>
                      <Input
                        id="absence-label-general"
                        placeholder="Enter custom label (optional)"
                        value={settings.absenceLabelOverride || ''}
                        onChange={(e) => handleAbsenceLabelChange(e.target.value)}
                        className="w-[200px]"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Open Demand Tab ─────────────────────────────────────────── */}
            <TabsContent value="open-demand" className="space-y-6">
              <Card className="border-primary/20">
                <CardContent className="space-y-8 pt-6">

                  {/* Intro */}
                  <div className="flex items-start gap-3 rounded-lg border bg-muted/40 px-4 py-3">
                    <Filter className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Configure how inbound resource requests are automatically categorised by urgency.
                      Thresholds are measured in <strong>calendar days</strong> from today to the
                      request's start date. Adjusting these lets your team's definition of "critical"
                      match your actual planning horizon.
                    </p>
                  </div>

                  {/* Live preview strip */}
                  <div>
                    <Label className="text-sm font-semibold mb-3 block">Priority Band Preview</Label>
                    <div className="grid grid-cols-4 gap-3">
                      {([
                        {
                          key: 'critical',
                          label: 'Critical',
                          desc: `Start date within ${settings.requestPriorityThresholds?.criticalDays ?? 7} days`,
                          accent: 'var(--accent-destructive-500)',
                          textClass: 'text-destructive',
                        },
                        {
                          key: 'high',
                          label: 'High',
                          desc: `${(settings.requestPriorityThresholds?.criticalDays ?? 7) + 1}–${settings.requestPriorityThresholds?.highDays ?? 14} days`,
                          accent: 'var(--accent-warning-500)',
                          textClass: 'text-[var(--accent-warning-600)]',
                        },
                        {
                          key: 'medium',
                          label: 'Medium',
                          desc: `${(settings.requestPriorityThresholds?.highDays ?? 14) + 1}–${settings.requestPriorityThresholds?.mediumDays ?? 30} days`,
                          accent: 'var(--accent-info-500)',
                          textClass: 'text-[var(--accent-info-600)]',
                        },
                        {
                          key: 'low',
                          label: 'Low',
                          desc: `More than ${settings.requestPriorityThresholds?.mediumDays ?? 30} days`,
                          accent: 'var(--accent-teal-500)',
                          textClass: 'text-[var(--accent-teal-600)]',
                        },
                      ] as const).map(band => (
                        <div
                          key={band.key}
                          className="rounded-lg border-l-4 border bg-background px-4 py-3 space-y-1"
                          style={{ borderLeftColor: band.accent }}
                        >
                          <p className={`text-sm font-semibold ${band.textClass}`}>{band.label}</p>
                          <p className="text-xs text-muted-foreground">{band.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Threshold inputs */}
                  <div>
                    <Label className="text-sm font-semibold mb-1 block">Threshold Configuration</Label>
                    <p className="text-xs text-muted-foreground mb-4">
                      Set the maximum number of days until a request's start date for each band.
                      Each value must be greater than the one above it.
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      {/* Critical */}
                      <div className="space-y-2">
                        <Label htmlFor="threshold-critical" className="flex items-center gap-2 text-sm">
                          <span className="h-2.5 w-2.5 rounded-full bg-destructive inline-block" />
                          Critical threshold (days)
                        </Label>
                        <Input
                          id="threshold-critical"
                          type="number"
                          min={1}
                          max={(settings.requestPriorityThresholds?.highDays ?? 14) - 1}
                          value={settings.requestPriorityThresholds?.criticalDays ?? 7}
                          onFocus={e => e.target.select()}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            if (isNaN(val) || val < 1) return;
                            const current = settings.requestPriorityThresholds ?? { criticalDays: 7, highDays: 14, mediumDays: 30 };
                            updateSettings({ requestPriorityThresholds: { ...current, criticalDays: val } });
                          }}
                        />
                        <p className="text-xs text-muted-foreground">Requests starting within this many days are <strong>Critical</strong></p>
                      </div>

                      {/* High */}
                      <div className="space-y-2">
                        <Label htmlFor="threshold-high" className="flex items-center gap-2 text-sm">
                          <span className="h-2.5 w-2.5 rounded-full bg-[var(--accent-warning-500)] inline-block" />
                          High threshold (days)
                        </Label>
                        <Input
                          id="threshold-high"
                          type="number"
                          min={(settings.requestPriorityThresholds?.criticalDays ?? 7) + 1}
                          max={(settings.requestPriorityThresholds?.mediumDays ?? 30) - 1}
                          value={settings.requestPriorityThresholds?.highDays ?? 14}
                          onFocus={e => e.target.select()}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            if (isNaN(val) || val < 1) return;
                            const current = settings.requestPriorityThresholds ?? { criticalDays: 7, highDays: 14, mediumDays: 30 };
                            updateSettings({ requestPriorityThresholds: { ...current, highDays: val } });
                          }}
                        />
                        <p className="text-xs text-muted-foreground">Requests starting within this many days are <strong>High</strong></p>
                      </div>

                      {/* Medium */}
                      <div className="space-y-2">
                        <Label htmlFor="threshold-medium" className="flex items-center gap-2 text-sm">
                          <span className="h-2.5 w-2.5 rounded-full bg-[var(--accent-info-500)] inline-block" />
                          Medium threshold (days)
                        </Label>
                        <Input
                          id="threshold-medium"
                          type="number"
                          min={(settings.requestPriorityThresholds?.highDays ?? 14) + 1}
                          value={settings.requestPriorityThresholds?.mediumDays ?? 30}
                          onFocus={e => e.target.select()}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            if (isNaN(val) || val < 1) return;
                            const current = settings.requestPriorityThresholds ?? { criticalDays: 7, highDays: 14, mediumDays: 30 };
                            updateSettings({ requestPriorityThresholds: { ...current, mediumDays: val } });
                          }}
                        />
                        <p className="text-xs text-muted-foreground">Requests starting within this many days are <strong>Medium</strong>. Above this is <strong>Low</strong></p>
                      </div>
                    </div>
                  </div>

                  {/* Reset */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => updateSettings({ requestPriorityThresholds: { criticalDays: 7, highDays: 14, mediumDays: 30 } })}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults (7 / 14 / 30 days)
                    </Button>
                  </div>

                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>

          {/* Sticky Footer */}
          {!embeddedMode && (
            <div
              className="fixed bottom-0 right-0 z-20 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[left] duration-200 ease-linear"
              style={{ left: sidebarLeft }}
            >
              <div className="flex h-16 items-center justify-between gap-4 px-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {generalHasUnsavedChanges && (
                    <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      You have unsaved changes
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    disabled={!generalHasUnsavedChanges || isSaving}
                    onClick={handleCancelSettings}
                    size="lg"
                    className="min-w-[100px]"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!generalHasUnsavedChanges || isSaving}
                    onClick={handleSaveAllSettings}
                    size="lg"
                    className="min-w-[100px]"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Cancel Confirmation Dialog */}
          <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes. Are you sure you want to discard them? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep editing</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmCancel}>
                  Discard changes
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ))}



      {/* Roles & Permissions - Only visible to users with viewRolesPermissions */}
      {activeTab === 'admin' && (!canViewRolesPermissions() ? <AccessDenied /> : (
        selectedRoleId === null ? (
          <RolesPermissionsList onEditRole={(roleId) => setSelectedRoleId(roleId)} />
        ) : selectedRole ? (
          <EditRoleProfile role={selectedRole} onBack={() => setSelectedRoleId(null)}>
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Role-specific settings for <strong>{selectedRole.name}</strong>. General admin settings have been moved to Settings &gt; General.
            </p>
          </div>
          </EditRoleProfile>
        ) : null
      ))}









    </div>
    </div>
  );
}

interface GroupSetupTabProps {
  currentMode: 'demo' | 'live';
  schedulerGroups: any[];
  resources: any[];
  addSchedulerGroup: (group: any) => Promise<void>;
  updateSchedulerGroup: (id: string, updates: any) => Promise<void>;
  saveSchedulerGroup: (id: string) => Promise<void>;
  deleteSchedulerGroup: (id: string) => Promise<void>;
}

function GroupSetupTab({ 
  currentMode, 
  schedulerGroups, 
  resources, 
  addSchedulerGroup, 
  updateSchedulerGroup, 
  saveSchedulerGroup,
  deleteSchedulerGroup 
}: GroupSetupTabProps) {
  const { state: sidebarState } = useSidebar();
  const sidebarLeft = sidebarState === 'collapsed' ? '3.5rem' : '13rem';
  const { refreshResourcesAndGroups } = useScheduler();
  const [searchQuery, setSearchQuery] = useState('');
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [openPickerGroupId, setOpenPickerGroupId] = useState<string | null>(null);
  const [resourceSearchQuery, setResourceSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: string; name: string; resourceCount: number } | null>(null);
  const [groupNameErrors, setGroupNameErrors] = useState<Record<string, string>>({});
  // Track which groups have unsaved changes
  const [dirtyGroupIds, setDirtyGroupIds] = useState<Set<string>>(new Set());
  const hasDirtyGroups = dirtyGroupIds.size > 0;
  // Track known group IDs so we can detect newly added groups
  const knownGroupIdsRef = useRef<Set<string>>(new Set(schedulerGroups.map(g => g.id)));
  // Track groups that were just created and haven't been manually edited yet
  const pristineGroupIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const names: Record<string, string> = {};
    schedulerGroups.forEach(g => { names[g.id] = g.name; });
    setGroupNames(names);

    // Detect newly added groups and mark them dirty, and clear any stale errors
    const currentIds = new Set(schedulerGroups.map(g => g.id));
    const newIds: string[] = [];
    currentIds.forEach(id => {
      if (!knownGroupIdsRef.current.has(id)) {
        newIds.push(id);
        setDirtyGroupIds(prev => new Set(prev).add(id));
        // Mark newly added groups as pristine so blur validation is skipped
        pristineGroupIdsRef.current.add(id);
      }
    });
    // Clear errors for newly added groups so they don't show stale validation
    if (newIds.length > 0) {
      setGroupNameErrors(prev => {
        const next = { ...prev };
        newIds.forEach(id => { delete next[id]; });
        return next;
      });
    }
    // Also remove deleted groups from dirty set
    setDirtyGroupIds(prev => {
      const next = new Set(prev);
      prev.forEach(id => { if (!currentIds.has(id)) next.delete(id); });
      return next;
    });
    knownGroupIdsRef.current = currentIds;
  }, [schedulerGroups]);

  const normalizeGroupName = (name: string): string => {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
  };

  const isGroupNameDuplicate = (name: string, excludeGroupId?: string): boolean => {
    if (!name || !name.trim()) return false;
    
    const normalizedName = normalizeGroupName(name);
    return schedulerGroups.some(g => 
      String(g.id) !== String(excludeGroupId) && 
      normalizeGroupName(g.name) === normalizedName
    );
  };

  const handleAddGroup = async () => {
    const defaultName = 'New Group';
    let finalName = defaultName;
    let counter = 1;

    while (isGroupNameDuplicate(finalName)) {
      finalName = `${defaultName} ${counter}`;
      counter++;
    }

    // addSchedulerGroup always creates locally (no API call).
    // The API call is deferred to Save via saveSchedulerGroup.
    await addSchedulerGroup({ 
      name: finalName, 
      resourceIds: [],
      avatarUrl: 'https://ui-avatars.com/api/?name=?&background=94a3b8&color=fff&size=40&bold=true'
    });
    // Pristine marking is handled by the useEffect above when it detects new group IDs
  };

  const handleAvatarUpload = async (groupId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const avatarUrl = reader.result as string;
      await updateSchedulerGroup(groupId, { avatarUrl });
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    };
    reader.readAsDataURL(file);
  };

  const handleGroupNameChange = (groupId: string, value: string) => {
    if (value.length <= 20) {
      setGroupNames(prev => ({ ...prev, [groupId]: value }));
      // Mark group as no longer pristine once the user edits the name
      pristineGroupIdsRef.current.delete(groupId);
      // Clear error when user starts editing
      if (groupNameErrors[groupId]) {
        setGroupNameErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[groupId];
          return newErrors;
        });
      }
    }
  };

  const handleGroupNameBlur = async (groupId: string) => {
    // Skip validation for pristine groups (just created, not yet edited by user)
    if (pristineGroupIdsRef.current.has(groupId)) {
      return;
    }

    const rawName = groupNames[groupId];
    // If groupNames hasn't synced yet, fall back to the persisted name
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    const name = (rawName ?? group?.name)?.trim().replace(/\s+/g, ' ');
    
    if (!name) {
      setGroupNameErrors(prev => ({ ...prev, [groupId]: 'Group name cannot be empty' }));
      setGroupNames(prev => ({ ...prev, [groupId]: group?.name || '' }));
      return;
    }

    if (name.length > 20) {
      setGroupNameErrors(prev => ({ ...prev, [groupId]: 'Group name cannot exceed 20 characters' }));
      setGroupNames(prev => ({ ...prev, [groupId]: group?.name || '' }));
      return;
    }
    
    if (name !== group?.name) {
      // Duplicate name validation is deferred to the Save button so that the
      // error does not appear prematurely while the user is still editing.
      await updateSchedulerGroup(groupId, { name });
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    if (group) {
      setGroupToDelete({
        id: group.id,
        name: group.name,
        resourceCount: group.resourceIds.length
      });
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteGroup = async () => {
    if (groupToDelete) {
      await deleteSchedulerGroup(groupToDelete.id);
      pristineGroupIdsRef.current.delete(groupToDelete.id);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      toast.success('Group deleted successfully');
    }
  };

  const cancelDeleteGroup = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const handleSaveAll = async () => {
    setIsSavingAll(true);
    try {
      const groupIdsToSave = Array.from(dirtyGroupIds);
      const savedIds = new Set<string>();

      // Validate duplicate names before saving
      let hasValidationErrors = false;
      for (const groupId of groupIdsToSave) {
        const group = schedulerGroups.find(g => String(g.id) === String(groupId));
        if (group && isGroupNameDuplicate(group.name, groupId)) {
          setGroupNameErrors(prev => ({ ...prev, [groupId]: 'A group with this name already exists. Please choose a different name.' }));
          hasValidationErrors = true;
        }
      }
      if (hasValidationErrors) {
        setIsSavingAll(false);
        return;
      }

      for (const groupId of groupIdsToSave) {
        try {
          await saveSchedulerGroup(groupId);
          savedIds.add(groupId);
          pristineGroupIdsRef.current.delete(groupId);
        } catch (err) {
          if (err instanceof ApiError && err.details) {
            setGroupNameErrors(prev => ({ ...prev, [groupId]: err.details! }));
          }
        }
      }
      setDirtyGroupIds(prev => {
        const next = new Set(prev);
        savedIds.forEach(id => next.delete(id));
        return next;
      });
    } finally {
      setIsSavingAll(false);
    }
  };

  const handleDiscard = async () => {
    // Revert unsaved (optimistic) edits by re-fetching groups from the server.
    await refreshResourcesAndGroups();
    setDirtyGroupIds(new Set());
    setGroupNameErrors({});
    pristineGroupIdsRef.current.clear();
  };

  const handleRemoveResource = async (groupId: string, resourceId: string) => {
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    if (group) {
      const updatedResourceIds = group.resourceIds.filter((id: string) => String(id) !== String(resourceId));
      await updateSchedulerGroup(groupId, { resourceIds: updatedResourceIds });
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    }
  };

  const handleToggleResource = async (groupId: string, resourceId: string) => {
    const group = schedulerGroups.find(g => String(g.id) === String(groupId));
    if (group) {
      const isSelected = group.resourceIds.includes(resourceId);
      const updatedResourceIds = isSelected
        ? group.resourceIds.filter((id: string) => String(id) !== String(resourceId))
        : [...group.resourceIds, resourceId];
      await updateSchedulerGroup(groupId, { resourceIds: updatedResourceIds });
      setDirtyGroupIds(prev => new Set(prev).add(groupId));
    }
  };

  const availableResources = resources.filter(r => String(r.id) !== 'unallocated');

  const getResourceName = (resourceId: string) => {
    const resource = availableResources.find(r => String(r.id) === String(resourceId));
    return resource?.name || 'Unknown';
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getFilteredPickerResources = () => {
    // Get all resource IDs assigned to OTHER groups (not the current group being edited)
    const assignedToOtherGroups = schedulerGroups
      .filter(g => String(g.id) !== String(openPickerGroupId))
      .flatMap(g => g.resourceIds);

    // Filter out resources that are assigned to other groups
    const unassignedResources = availableResources.filter(
      r => !assignedToOtherGroups.includes(r.id)
    );

    // Apply search filter if query exists
    if (!resourceSearchQuery) return unassignedResources;
    const query = resourceSearchQuery.toLowerCase();
    return unassignedResources.filter(
      (r) => r.name.toLowerCase().includes(query) || r.role.toLowerCase().includes(query)
    );
  };

  const filteredGroups = searchQuery
    ? schedulerGroups.filter(group => {
        const nameMatch = group.name.toLowerCase().includes(searchQuery.toLowerCase());
        const resourceMatch = group.resourceIds.some((id: string) =>
          getResourceName(id).toLowerCase().includes(searchQuery.toLowerCase())
        );
        return nameMatch || resourceMatch;
      })
    : schedulerGroups;

  return (
    <Card className="border-primary/20">
    <CardContent className="space-y-6 pt-6 pb-24">
      {/* Section Header */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={handleAddGroup}
          >
            <Plus className="h-4 w-4" />
            Add Planner Group
          </Button>
        </div>
      </div>

      <Separator />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search planner groups or resources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {filteredGroups.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              {schedulerGroups.length === 0
                ? 'No planner groups yet'
                : 'No planner groups match your search'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {schedulerGroups.length === 0
                ? 'Click "Add Planner Group" to create your first planner group.'
                : 'Try adjusting your search query.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredGroups.map((group) => {
            const currentName = groupNames[group.id] ?? group.name;
            return (
              <Card key={group.id} className="relative overflow-hidden">
                <CardContent className="space-y-4 pt-5 pb-5">
                  {/* Delete button */}
                  <div className="absolute top-3 right-3 flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteGroup(group.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Avatar & Group Name */}
                  <div className="flex items-start gap-4 pr-10">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Avatar</Label>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-11 w-11 rounded-lg ring-1 ring-border">
                          <AvatarImage src={group.avatarUrl} alt={group.name} />
                          <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-base font-semibold">?</AvatarFallback>
                        </Avatar>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/jpeg,image/png,image/gif,image/webp';
                            input.onchange = (event) => handleAvatarUpload(group.id, event as any);
                            input.click();
                          }}
                        >
                          <Upload className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex-1 space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Group Name</Label>
                      <Input
                        value={currentName}
                        onChange={(e) => handleGroupNameChange(group.id, e.target.value)}
                        onFocus={(e) => {
                          e.target.setSelectionRange(0, e.target.value.length);
                        }}
                        onBlur={() => handleGroupNameBlur(group.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                        className={cn(
                          'h-9 text-sm',
                          groupNameErrors[group.id] && 'border-destructive focus-visible:ring-destructive/50'
                        )}
                        placeholder="Enter group name"
                        aria-invalid={!!groupNameErrors[group.id]}
                        aria-describedby={groupNameErrors[group.id] ? `error-${group.id}` : undefined}
                      />
                      {groupNameErrors[group.id] ? (
                        <p id={`error-${group.id}`} className="text-xs text-destructive" role="alert">
                          {groupNameErrors[group.id]}
                        </p>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {currentName.length}/20 characters
                        </span>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Assigned Resources */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Assigned Resources</Label>
                    <Popover
                      open={openPickerGroupId === group.id}
                      onOpenChange={(open) => {
                        setOpenPickerGroupId(open ? group.id : null);
                        if (!open) setResourceSearchQuery('');
                      }}
                    >
                      <PopoverTrigger asChild>
                        <button className="w-full rounded-lg border border-input min-h-[42px] p-2 flex flex-wrap items-center gap-1.5 cursor-pointer hover:bg-accent/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                          {group.resourceIds.length === 0 ? (
                            <span className="text-sm text-muted-foreground px-1 flex items-center gap-2">
                              <User className="h-4 w-4 opacity-50" />
                              Select resources...
                            </span>
                          ) : (
                            group.resourceIds.map((resourceId: string) => (
                              <Badge
                                key={resourceId}
                                className="gap-1 text-sm px-3 py-1"
                                onClose={() => handleRemoveResource(group.id, resourceId)}
                              >
                                {getResourceName(resourceId)}
                              </Badge>
                            ))
                          )}
                          <ChevronsUpDown className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <div className="flex items-center border-b px-3">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <input
                              placeholder="Search by name or role..."
                              value={resourceSearchQuery}
                              onChange={(e) => setResourceSearchQuery(e.target.value)}
                              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            />
                          </div>
                          <CommandList className="max-h-[300px]">
                            <CommandEmpty>
                              <div className="py-6 text-center">
                                <div className="rounded-full bg-muted p-3 mx-auto w-fit mb-3">
                                  <User className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {resourceSearchQuery 
                                    ? 'No resources match your search' 
                                    : 'All resources are already assigned to groups'}
                                </p>
                                {!resourceSearchQuery && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Remove resources from other groups to make them available
                                  </p>
                                )}
                              </div>
                            </CommandEmpty>
                            <CommandGroup>
                              {getFilteredPickerResources().map((resource) => {
                                const isSelected = group.resourceIds.includes(resource.id);
                                return (
                                  <CommandItem
                                    key={resource.id}
                                    value={resource.id}
                                    onSelect={() => handleToggleResource(group.id, resource.id)}
                                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                                  >
                                    <div
                                      className={cn(
                                        'flex h-5 w-5 items-center justify-center rounded border border-primary shrink-0',
                                        isSelected
                                          ? 'bg-primary text-primary-foreground'
                                          : 'opacity-50 [&_svg]:invisible'
                                      )}
                                    >
                                      <Check className="h-4 w-4" />
                                    </div>
                                    <Avatar className="h-8 w-8 rounded-md shrink-0">
                                      <AvatarImage src={resource.photoUrl} alt={resource.name} />
                                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                                        {getInitials(resource.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{resource.name}</p>
                                      <p className="text-xs text-muted-foreground truncate">{resource.role}</p>
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <span className="text-xs text-muted-foreground">
                      {group.resourceIds.length} resource(s)
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Group Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(isOpen) => !isOpen && cancelDeleteGroup()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Planner Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the planner group{' '}
              <span className="font-semibold text-foreground">
                &ldquo;{groupToDelete?.name}&rdquo;
              </span>
              ?{' '}
              {groupToDelete && groupToDelete.resourceCount > 0 && (
                <>
                  This group has{' '}
                  <span className="font-semibold text-foreground">
                    {groupToDelete.resourceCount} resource(s)
                  </span>{' '}
                  assigned that will be unassigned.{' '}
                </>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteGroup}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Planner Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SettingsSaveBar
        isDirty={hasDirtyGroups}
        isSaving={isSavingAll}
        onSave={handleSaveAll}
        onDiscard={handleDiscard}
        sidebarLeft={sidebarLeft}
      />
    </CardContent>
    </Card>
  );
}
